import type BaseLayer from 'ol/layer/Base';
import type { GeoJSONObject } from 'ol/format/GeoJSON';
import type { Projection as OLProjection } from 'ol/proj';
import { getCenter } from 'ol/extent';

import {
  VALID_PROJECTION_CODES,
  type Extent,
  type TypeFeatureInfoEntry,
  type TypeFeatureInfoEntryPartial,
} from '@/api/types/map-schema-types';
import type {
  TypeGeoviewLayerConfig,
  TypeLayerEntryConfig,
  TypeLayerStatus,
  TypeMosaicMethod,
  TypeMosaicOperation,
  TypeMosaicRule,
} from '@/api/types/layer-schema-types';
import type { EventDelegateBase } from '@/api/events/event-helper';
import EventHelper from '@/api/events/event-helper';
import type { ConfigBaseClass } from '@/api/config/validation-classes/config-base-class';
import type { AbstractBaseLayerEntryConfig } from '@/api/config/validation-classes/abstract-base-layer-entry-config';
import type { GroupLayerEntryConfig } from '@/api/config/validation-classes/group-layer-entry-config';
import { AbstractMapViewerController } from '@/core/controllers/base/abstract-map-viewer-controller';
import type { ControllerRegistry } from '@/core/controllers/base/controller-registry';
import { LayerNotEsriDynamicError, LayerNotGeoJsonError, LayerWrongTypeError } from '@/core/exceptions/layer-exceptions';
import {
  getStoreLayerBounds,
  getStoreLayerMosaicRule,
  getStoreLayerHighlightedLayer,
  getStoreLayerLegendLayerByPath,
  getStoreLayerOrderedLayerIndexByPath,
  getStoreLayerOrderedLayerPaths,
  setStoreLayerAllMapLayerCollapsed,
  setStoreLayerBounds,
  setStoreLayerDateTemporal,
  setStoreLayerDeletionStartTime,
  setStoreLayerDisplayDateFormat,
  setStoreLayerDisplayDateFormatShort,
  setStoreLayerHighlightedLayer,
  setStoreLayerHoverable,
  setStoreLayerItemVisibility,
  setStoreLayerMosaicRule,
  setStoreLayerName,
  setStoreLayerQueryable,
  setStoreLayerRasterFunction,
  setStoreLayersAreLoading,
  setStoreLayerTextVisibility,
  setStoreLayerWmsStyle,
  setStoreLayerOpacity,
  setStoreLayerDisplayState,
  setStoreLayerInVisibleRange,
  setStoreLayerVisibility,
  setStoreLayerLegendCollapsed,
  setStoreLayerOrderedLayers,
  setStoreLayerSelectedLayersTabLayer,
  utilFindLayerAndChildrenPaths,
} from '@/core/stores/states/layer-state';
import { getStoreAppShowLayerHighlightLayerBbox } from '@/core/stores/states/app-state';
import { setStoreDataTableFilter } from '@/core/stores/states/data-table-state';
import { isStoreTimeSliderInitialized, setStoreTimeSliderFilter } from '@/core/stores/states/time-slider-state';
import type {
  DomainLayerBaseDelegate,
  DomainLayerBaseEvent,
  DomainLayerFilterAppliedDelegate,
  DomainLayerFilterAppliedEvent,
  DomainLayerGroupChildrenUpdatedEvent,
  DomainLayerGroupChildrenUpdatedDelegate,
  DomainLayerHoverableChangedDelegate,
  DomainLayerHoverableChangedEvent,
  DomainLayerItemVisibilityChangedDelegate,
  DomainLayerItemVisibilityChangedEvent,
  DomainLayerMessageDelegate,
  DomainLayerMessageEvent,
  DomainLayerMosaicRuleChangedDelegate,
  DomainLayerMosaicRuleChangedEvent,
  DomainLayerNameChangedDelegate,
  DomainLayerNameChangedEvent,
  DomainLayerOpacityChangedDelegate,
  DomainLayerOpacityChangedEvent,
  DomainLayerQueryableChangedDelegate,
  DomainLayerQueryableChangedEvent,
  DomainLayerRasterFunctionChangedDelegate,
  DomainLayerRasterFunctionChangedEvent,
  DomainLayerRegisteredDelegate,
  DomainLayerRegisteredEvent,
  DomainLayerStatusChangedDelegate,
  DomainLayerStatusChangedEvent,
  DomainLayerVisibleChangedDelegate,
  DomainLayerVisibleChangedEvent,
  DomainLayerImageLoadRescueEvent,
  DomainLayerImageLoadRescueDelegate,
  DomainLayerWMSStyleChangedDelegate,
  DomainLayerWMSStyleChangedEvent,
  LayerDomain,
} from '@/core/domains/layer-domain';
import type { UIDomain } from '@/core/domains/ui-domain';
import { doTimeout, doUntilPromise, isValidUUID, type DelayJob } from '@/core/utils/utilities';
import type { TemporalMode, TypeDisplayDateFormat } from '@/core/utils/date-mgt';
import type { TypeLayersViewDisplayState, TypeLegendItem } from '@/core/components/layers/types';
import { logger } from '@/core/utils/logger';
import { NoBoundsError, NoExtentError } from '@/core/exceptions/geoview-exceptions';
import { OL_ZOOM_DURATION, OL_ZOOM_PERCENT_PADDING, type GVFitOptions } from '@/core/utils/constant';
import { Projection } from '@/geo/utils/projection';
import { GeoUtilities } from '@/geo/utils/utilities';
import {
  MapViewer,
  type MapBaseEvent,
  type MapLayersProcessedDelegate,
  type MapMoveEndDelegate,
  type MapMoveEndEvent,
} from '@/geo/map/map-viewer';
import { GVEsriImage } from '@/geo/layer/gv-layers/raster/gv-esri-image';
import type { AbstractBaseGVLayer } from '@/geo/layer/gv-layers/abstract-base-layer';
import { AbstractGVLayer } from '@/geo/layer/gv-layers/abstract-gv-layer';
import { GVGroupLayer } from '@/geo/layer/gv-layers/gv-group-layer';
import { AbstractGVVector } from '@/geo/layer/gv-layers/vector/abstract-gv-vector';
import { AbstractGVRaster } from '@/geo/layer/gv-layers/raster/abstract-gv-raster';
import { GVKML } from '@/geo/layer/gv-layers/vector/gv-kml';
import { GVWMS } from '@/geo/layer/gv-layers/raster/gv-wms';
import { GVGeoJSON } from '@/geo/layer/gv-layers/vector/gv-geojson';
import { GVEsriDynamic } from '@/geo/layer/gv-layers/raster/gv-esri-dynamic';

/**
 * LayerController class that extends the AbstractMapViewerController and provides methods to interact with map layers.
 */
export class LayerController extends AbstractMapViewerController {
  /** The opacity ratio to use when highlighting a layer vs the other layers */
  static readonly HIGHLIGHT_OPACITY_RATIO = 4;

  /** The timeout duration for metadata refresh in milliseconds */
  static readonly #METADATA_REFRESHED_TIMEOUT = 5000; // When the metadata is already fresh of 5 seconds, we consider it as still fresh and we avoid trying to refresh it again to prevent multiple rapid refresh attempts.

  /** The UI Domain instance associated with this controller */
  #uiDomain: UIDomain;

  /** The Layer Domain instance associated with this controller */
  #layerDomain: LayerDomain;

  /** Used to keep a reference of highlighted layer */
  #highlightedLayerPath = '';

  /** Stores the original opacity of the highlighted layer (and its leaf children for groups) so it can be restored on unhighlight. */
  #highlightedLayerOriginalOpacity: Map<string, number> = new Map();

  /** Holds all the layers latest date they had their metadata refreshed */
  #layersBeingMetadataRefreshed: Record<string, number> = {};

  /** Holds all the layers in process of being deleted from the map */
  #layersBeingDeleted: Record<string, LayerDeletionJob> = {};

  /** The bounded reference to the handle map layers processed event */
  #boundedHandleMapLayersProcessed: MapLayersProcessedDelegate;

  /** The bounded reference to the handle map move end event */
  #boundedHandleMapMoveEnd: MapMoveEndDelegate;

  /** The bounded reference to the handle layer entry config registered */
  #boundedHandleDomainLayerEntryConfigRegistered: DomainLayerStatusChangedDelegate;

  /** The bounded reference to the handle layer entry config unregistered */
  #boundedHandleDomainLayerEntryConfigUnregistered: DomainLayerStatusChangedDelegate;

  /** The bounded reference to the handle layer registered */
  #boundedHandleDomainLayerRegistered: DomainLayerRegisteredDelegate;

  /** The bounded reference to the handle layer unregistered */
  #boundedHandleDomainLayerUnregistered: DomainLayerRegisteredDelegate;

  /** The bounded reference to the handle layer name changed */
  #boundedHandleDomainLayerNameChanged: DomainLayerNameChangedDelegate;

  /** The bounded reference to the handle layer visibility changed */
  #boundedHandleDomainLayerVisibleChanged: DomainLayerVisibleChangedDelegate;

  /** The bounded reference to the handle layer opacity changed */
  #boundedHandleDomainLayerOpacityChanged: DomainLayerOpacityChangedDelegate;

  /** The bounded reference to the handle layer loading changed */
  #boundedHandleDomainLayerLoadingChanged: DomainLayerBaseDelegate;

  /** The bounded reference to the handle layer first loaded changed */
  #boundedHandleDomainLayerFirstLoadedChanged: DomainLayerBaseDelegate;

  /** The bounded reference to the handle layer loaded changed */
  #boundedHandleDomainLayerLoadedChanged: DomainLayerBaseDelegate;

  /** The bounded reference to the handle all layers loaded */
  #boundedHandleDomainLayerAllLoaded: DomainLayerStatusChangedDelegate;

  /** The bounded reference to the handle layer message event */
  #boundedHandleDomainLayerMessage: DomainLayerMessageDelegate;

  /** The bounded reference to the handle layer hoverable changed */
  #boundedHandleDomainLayerHoverableChanged: DomainLayerHoverableChangedDelegate;

  /** The bounded reference to the handle layer queryable changed */
  #boundedHandleDomainLayerQueryableChanged: DomainLayerQueryableChangedDelegate;

  /** The bounded reference to the handle layer item visibility changed */
  #boundedHandleDomainLayerItemVisibilityChanged: DomainLayerItemVisibilityChangedDelegate;

  /** The bounded reference to the handle layer filter applied */
  #boundedHandleDomainLayerFilterApplied: DomainLayerFilterAppliedDelegate;

  /** The bounded reference to the handle layer group layer added */
  #boundedHandleDomainLayerGroupLayerAdded: DomainLayerGroupChildrenUpdatedDelegate;

  /** The bounded reference to the handle layer group layer removed */
  #boundedHandleDomainLayerGroupLayerRemoved: DomainLayerGroupChildrenUpdatedDelegate;

  /** The bounded reference to the handle Layer Image Load Callbacks */
  #boundedHandleDomainLayerImageLoadRescue: DomainLayerImageLoadRescueDelegate;

  /** The bounded reference to the handle WMS style changed */
  #boundedHandleDomainLayerWMSStyleChanged: DomainLayerWMSStyleChangedDelegate;

  /** The bounded reference to the handle raster function changed */
  #boundedHandleDomainLayerRasterFunctionChanged: DomainLayerRasterFunctionChangedDelegate;

  /** The bounded reference to the handle mosaic rule changed */
  #boundedHandleDomainLayerMosaicRuleChanged: DomainLayerMosaicRuleChangedDelegate;

  /** Callback delegates for the layer item visibility toggled event */
  #onLayerItemVisibilityToggledHandlers: ControllerLayerItemVisibilityChangedDelegate[] = [];

  /**
   * Creates an instance of LayerController.
   *
   * @param mapViewer - The map viewer instance to associate with this controller
   * @param controllerRegistry - The controller registry for accessing sibling controllers
   * @param layerDomain - The layer domain instance to associate with this controller
   * @param uiDomain - The UI domain instance to associate with this controller
   */
  constructor(mapViewer: MapViewer, controllerRegistry: ControllerRegistry, layerDomain: LayerDomain, uiDomain: UIDomain) {
    super(mapViewer, controllerRegistry);

    // Keep the domain internally
    this.#layerDomain = layerDomain;

    // Keep the UI domain internally
    this.#uiDomain = uiDomain;

    // Keep a bounded reference to the handle map move end method
    this.#boundedHandleMapMoveEnd = this.#handleMapMoveEnd.bind(this);

    // Keep a bounded reference to the handle map layers processed event
    this.#boundedHandleMapLayersProcessed = this.#handleMapLayersProcessed.bind(this);

    // Keep a bounded reference to the handle layer entry config registered
    this.#boundedHandleDomainLayerEntryConfigRegistered = this.#handleDomainLayerEntryConfigRegistered.bind(this);

    // Keep a bounded reference to the handle layer entry config unregistered
    this.#boundedHandleDomainLayerEntryConfigUnregistered = this.#handleDomainLayerEntryConfigUnregistered.bind(this);

    // Keep a bounded reference to the handle layer registered
    this.#boundedHandleDomainLayerRegistered = this.#handleDomainLayerRegistered.bind(this);

    // Keep a bounded reference to the handle layer unregistered
    this.#boundedHandleDomainLayerUnregistered = this.#handleDomainLayerUnregistered.bind(this);

    // Keep a bounded reference to the handle layer name changed
    this.#boundedHandleDomainLayerNameChanged = this.#handleDomainLayerNameChanged.bind(this);

    // Keep a bounded reference to the handle layer visibility changed
    this.#boundedHandleDomainLayerVisibleChanged = this.#handleDomainLayerVisibleChanged.bind(this);

    // Keep a bounded reference to the handle layer opacity changed
    this.#boundedHandleDomainLayerOpacityChanged = this.#handleDomainLayerOpacityChanged.bind(this);

    // Keep a bounded reference to the handle layer loading changed
    this.#boundedHandleDomainLayerLoadingChanged = this.#handleDomainLayerLoadingChanged.bind(this);

    // Keep a bounded reference to the handle layer first loaded changed
    this.#boundedHandleDomainLayerFirstLoadedChanged = this.#handleDomainLayerFirstLoadedChanged.bind(this);

    // Keep a bounded reference to the handle layer loaded changed
    this.#boundedHandleDomainLayerLoadedChanged = this.#handleDomainLayerLoadedChanged.bind(this);

    // Keep a bounded reference to the handle layer all loaded changed
    this.#boundedHandleDomainLayerAllLoaded = this.#handleDomainLayerAllLoaded.bind(this);

    // Keep a bounded reference to the handle layer message event
    this.#boundedHandleDomainLayerMessage = this.#handleDomainLayerMessage.bind(this);

    // Keep a bounded reference to the handle layer hoverable changed
    this.#boundedHandleDomainLayerHoverableChanged = this.#handleDomainLayerHoverableChanged.bind(this);

    // Keep a bounded reference to the handle layer queryable changed
    this.#boundedHandleDomainLayerQueryableChanged = this.#handleDomainLayerQueryableChanged.bind(this);

    // Keep a bounded reference to the handle layer item visibility changed
    this.#boundedHandleDomainLayerItemVisibilityChanged = this.#handleDomainLayerItemVisibilityChanged.bind(this);

    // Keep a bounded reference to the handle layer filter applied
    this.#boundedHandleDomainLayerFilterApplied = this.#handleDomainLayerFilterApplied.bind(this);

    // Keep a bounded reference to the handle layer group layer added
    this.#boundedHandleDomainLayerGroupLayerAdded = this.#handleDomainLayerGroupLayerAdded.bind(this);

    // Keep a bounded reference to the handle layer group layer removed
    this.#boundedHandleDomainLayerGroupLayerRemoved = this.#handleDomainLayerGroupLayerRemoved.bind(this);

    // Keep a bounded reference to the handle Layer Image Load Callbacks
    this.#boundedHandleDomainLayerImageLoadRescue = this.#handleDomainLayerImageLoadRescue.bind(this);

    // Keep a bounded reference to the handle WMS style changed
    this.#boundedHandleDomainLayerWMSStyleChanged = this.#handleDomainLayerWMSStyleChanged.bind(this);

    // Keep a bounded reference to the handle layer raster function changed
    this.#boundedHandleDomainLayerRasterFunctionChanged = this.#handleDomainLayerRasterFunctionChanged.bind(this);

    // Keep a bounded reference to the handle layer mosaic rule changed
    this.#boundedHandleDomainLayerMosaicRuleChanged = this.#handleDomainLayerMosaicRuleChanged.bind(this);
  }

  // #region OVERRIDES

  /**
   * Hooks layer domain listeners.
   */
  protected override onHook(): void {
    // Listens when the map move ends
    this.getMapViewer().onMapMoveEnd(this.#boundedHandleMapMoveEnd);

    // Listens when the map layers have been processed
    this.getMapViewer().onMapLayersProcessed(this.#boundedHandleMapLayersProcessed);

    // Listens when a layer config has been registered in the domain
    this.#layerDomain.onLayerEntryConfigRegistered(this.#boundedHandleDomainLayerEntryConfigRegistered);

    // Listens when a layer config has been unregistered from the domain
    this.#layerDomain.onLayerEntryConfigUnregistered(this.#boundedHandleDomainLayerEntryConfigUnregistered);

    // Listens when a gv layer has been registered in the domain
    this.#layerDomain.onLayerRegistered(this.#boundedHandleDomainLayerRegistered);

    // Listens when a gv layer has been unregistered from the domain
    this.#layerDomain.onLayerUnregistered(this.#boundedHandleDomainLayerUnregistered);

    // Listens when the layer name is changed in the Layer domain
    this.#layerDomain.onLayerNameChanged(this.#boundedHandleDomainLayerNameChanged);

    // Listens when the layer visibility is changed in the Layer domain
    this.#layerDomain.onLayerVisibleChanged(this.#boundedHandleDomainLayerVisibleChanged);

    // Listens when the layer opacity is changed in the Layer domain
    this.#layerDomain.onLayerOpacityChanged(this.#boundedHandleDomainLayerOpacityChanged);

    // Listens when the layer loading state is changed in the Layer domain
    this.#layerDomain.onLayerLoading(this.#boundedHandleDomainLayerLoadingChanged);

    // Listens when the layer loading state is changed in the Layer domain
    this.#layerDomain.onLayerFirstLoaded(this.#boundedHandleDomainLayerFirstLoadedChanged);

    // Listens when the layer loaded state is changed in the Layer domain
    this.#layerDomain.onLayerLoaded(this.#boundedHandleDomainLayerLoadedChanged);

    // Listens when the layers are all loaded in the Layer domain
    this.#layerDomain.onLayerAllLoaded(this.#boundedHandleDomainLayerAllLoaded);

    // Listens when the layers have a message to inform
    this.#layerDomain.onLayerMessage(this.#boundedHandleDomainLayerMessage);

    // Listens when the layer hoverable state is changed in the Layer domain
    this.#layerDomain.onLayerHoverableChanged(this.#boundedHandleDomainLayerHoverableChanged);

    // Listens when the layer queryable state is changed in the Layer domain
    this.#layerDomain.onLayerQueryableChanged(this.#boundedHandleDomainLayerQueryableChanged);

    // Listens when the layer item visibility state is changed in the Layer domain
    this.#layerDomain.onLayerItemVisibilityChanged(this.#boundedHandleDomainLayerItemVisibilityChanged);

    // Listens when the layer item visibility state is changed in the Layer domain
    this.#layerDomain.onLayerFilterApplied(this.#boundedHandleDomainLayerFilterApplied);

    // Listens when a layer is added to a group layer in the Layer domain
    this.#layerDomain.onLayerGroupLayerAdded(this.#boundedHandleDomainLayerGroupLayerAdded);

    // Listens when a layer is added to a group layer in the Layer domain
    this.#layerDomain.onLayerGroupLayerRemoved(this.#boundedHandleDomainLayerGroupLayerRemoved);

    // Listens when an image load rescue event occurs
    this.#layerDomain.onLayerImageLoadRescue(this.#boundedHandleDomainLayerImageLoadRescue);

    // Listens when a WMS style is changed in the Layer domain
    this.#layerDomain.onLayerWmsStyleChanged(this.#boundedHandleDomainLayerWMSStyleChanged);

    // Listens when a layer raster function is changed in the Layer domain
    this.#layerDomain.onLayerRasterFunctionChanged(this.#boundedHandleDomainLayerRasterFunctionChanged);

    // Listens when a layer mosaic rule is changed in the Layer domain
    this.#layerDomain.onLayerMosaicRuleChanged(this.#boundedHandleDomainLayerMosaicRuleChanged);
  }

  /**
   * Unhooks the controller from the action.
   */
  protected override onUnhook(): void {
    // Unhooks when a layer mosaic rule is changed in the Layer domain
    this.#layerDomain.offLayerMosaicRuleChanged(this.#boundedHandleDomainLayerMosaicRuleChanged);

    // Unhooks when a layer raster function is changed in the Layer domain
    this.#layerDomain.offLayerRasterFunctionChanged(this.#boundedHandleDomainLayerRasterFunctionChanged);

    // Unhooks when a WMS style is changed in the Layer domain
    this.#layerDomain.offLayerWmsStyleChanged(this.#boundedHandleDomainLayerWMSStyleChanged);

    // Unhooks when an image load rescue event occurs
    this.#layerDomain.offLayerImageLoadRescue(this.#boundedHandleDomainLayerImageLoadRescue);

    // Unhooks when a layer is added to a group layer in the Layer domain
    this.#layerDomain.offLayerGroupLayerRemoved(this.#boundedHandleDomainLayerGroupLayerRemoved);

    // Unhooks when a layer is added to a group layer in the Layer domain
    this.#layerDomain.offLayerGroupLayerAdded(this.#boundedHandleDomainLayerGroupLayerAdded);

    // Listens when the layer item visibility state is changed in the Layer domain
    this.#layerDomain.offLayerFilterApplied(this.#boundedHandleDomainLayerFilterApplied);

    // Listens when the layer item visibility state is changed in the Layer domain
    this.#layerDomain.offLayerItemVisibilityChanged(this.#boundedHandleDomainLayerItemVisibilityChanged);

    // Unhooks when the layer queryable state is changed in the Layer domain
    this.#layerDomain.offLayerQueryableChanged(this.#boundedHandleDomainLayerQueryableChanged);

    // Unhooks when the layer hoverable state is changed in the Layer domain
    this.#layerDomain.offLayerHoverableChanged(this.#boundedHandleDomainLayerHoverableChanged);

    // Unhooks when the layer have a message to inform
    this.#layerDomain.offLayerMessage(this.#boundedHandleDomainLayerLoadedChanged);

    // Unhooks when the layers are all loaded in the Layer domain
    this.#layerDomain.offLayerAllLoaded(this.#boundedHandleDomainLayerAllLoaded);

    // Unhooks when the layer loaded state is changed in the Layer domain
    this.#layerDomain.offLayerLoaded(this.#boundedHandleDomainLayerLoadedChanged);

    // Unhooks when the layer first loaded state is changed in the Layer domain
    this.#layerDomain.offLayerFirstLoaded(this.#boundedHandleDomainLayerFirstLoadedChanged);

    // Unhooks when the layer loading state is changed in the Layer domain
    this.#layerDomain.offLayerLoading(this.#boundedHandleDomainLayerLoadingChanged);

    // Unhooks when the layer opacity is changed in the Layer domain
    this.#layerDomain.offLayerOpacityChanged(this.#boundedHandleDomainLayerOpacityChanged);

    // Unhooks when the layer visibility is changed in the Layer domain
    this.#layerDomain.offLayerVisibleChanged(this.#boundedHandleDomainLayerVisibleChanged);

    // Unhooks when a layer name is changed in the Layer domain
    this.#layerDomain.offLayerNameChanged(this.#boundedHandleDomainLayerNameChanged);

    // Unhooks when a gv layer has been registered in the domain
    this.#layerDomain.offLayerUnregistered(this.#boundedHandleDomainLayerUnregistered);

    // Unhooks when a gv layer has been unregistered from the domain
    this.#layerDomain.offLayerRegistered(this.#boundedHandleDomainLayerRegistered);

    // Unhooks when a layer config has been unregistered from the domain
    this.#layerDomain.offLayerEntryConfigUnregistered(this.#boundedHandleDomainLayerEntryConfigUnregistered);

    // Unhooks when a layer config has been registered in the domain
    this.#layerDomain.offLayerEntryConfigRegistered(this.#boundedHandleDomainLayerEntryConfigRegistered);

    // Unhooks when the map layers have been processed
    this.getMapViewer().offMapLayersProcessed(this.#boundedHandleMapLayersProcessed);

    // Unhooks when the map move ends
    this.getMapViewer().offMapMoveEnd(this.#boundedHandleMapMoveEnd);
  }

  // #endregion OVERRIDES

  // #region PUBLIC METHODS - DOMAIN SIMPLE GETTERS

  /**
   * Gets the GeoView Layer Ids / UUIDs.
   *
   * @returns The ids of the layers
   */
  getGeoviewLayerIds(): string[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerIds();
  }

  /**
   * Gets the Layer Entry layer paths.
   *
   * @returns The GeoView Layer Paths
   */
  getLayerEntryLayerPaths(): string[] {
    // Retrieve from the domain
    return this.#layerDomain.getLayerEntryLayerPaths();
  }

  /**
   * Gets the Layer Entry Configs.
   *
   * @returns The GeoView Layer Entry Configs
   */
  getLayerEntryConfigs(): ConfigBaseClass[] {
    // Retrieve from the domain
    return this.#layerDomain.getLayerEntryConfigs();
  }

  /**
   * Retrieves the layer entry configuration for the given layer path.
   *
   * @param layerPath - The layer path to look up
   * @returns The ConfigBaseClass layer configuration
   * @throws {LayerConfigNotFoundError} When the layer configuration couldn't be found at the given layer path
   */
  getLayerEntryConfig(layerPath: string): ConfigBaseClass {
    // Retrieve from the domain
    return this.#layerDomain.getLayerEntryConfig(layerPath);
  }

  /**
   * Retrieves the layer entry configuration for the given layer path, if it exists.
   *
   * @param layerPath - The layer path to look up
   * @returns The ConfigBaseClass layer configuration, or undefined if not found
   */
  getLayerEntryConfigIfExists(layerPath: string): ConfigBaseClass | undefined {
    // Retrieve from the domain
    return this.#layerDomain.getLayerEntryConfigIfExists(layerPath);
  }

  /**
   * Gets the layer configuration of a regular layer (not a group) at the specified layer path.
   *
   * @param layerPath - The layer path
   * @returns The layer configuration
   * @throws {LayerConfigNotFoundError} When the layer configuration couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer configuration is of the wrong type at the given layer path
   */
  getLayerEntryConfigRegular(layerPath: string): AbstractBaseLayerEntryConfig {
    return this.#layerDomain.getLayerEntryConfigRegular(layerPath);
  }

  /**
   * Gets the layer configuration of a group layer (not a regular) at the specified layer path.
   *
   * @param layerPath - The layer path
   * @returns The layer configuration
   * @throws {LayerConfigNotFoundError} When the layer configuration couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer configuration is of the wrong type at the given layer path
   */
  getLayerEntryConfigGroup(layerPath: string): GroupLayerEntryConfig {
    return this.#layerDomain.getLayerEntryConfigGroup(layerPath);
  }

  /**
   * Gets the GeoView Layer Paths.
   *
   * @returns The layer paths of the GV Layers
   */
  getGeoviewLayerPaths(): string[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerPaths();
  }

  /**
   * Gets all GeoView layers.
   *
   * @returns The list of new Geoview Layers
   */
  getGeoviewLayers(): AbstractBaseGVLayer[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayers();
  }

  /**
   * Gets all GeoView layers that are regular layers (not groups).
   *
   * This method filters the list returned by `getGeoviewLayers()` and
   * returns only the layers that are instances of `AbstractGVLayer`.
   *
   * @returns An array containing only the regular layers from the current GeoView layer collection
   */
  getGeoviewLayersRegulars(): AbstractGVLayer[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayersRegulars();
  }

  /**
   * Gets all GeoView layers that are group layers.
   *
   * This method filters the list returned by `getGeoviewLayers()` and
   * returns only the layers that are instances of `GVGroupLayer`.
   *
   * @returns An array containing only the group layers from the current GeoView layer collection
   */
  getGeoviewLayersGroups(): GVGroupLayer[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayersGroups();
  }

  /**
   * Gets all GeoView layers that are at the root.
   *
   * @returns An array containing only the layers at the root level of the registry
   */
  getGeoviewLayersRoot(): AbstractBaseGVLayer[] {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayersRoot();
  }

  /**
   * Retrieves the Geoview layer for the given layer path.
   *
   * @param layerPath - The layer path to look up
   * @returns The Geoview layer
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   */
  getGeoviewLayer(layerPath: string): AbstractBaseGVLayer {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayer(layerPath);
  }

  /**
   * Retrieves the Geoview layer for the given layer path, if it exists.
   *
   * @param layerPath - The layer path to look up
   * @returns The AbstractBaseGVLayer or undefined when not found
   */
  getGeoviewLayerIfExists(layerPath: string): AbstractBaseGVLayer | undefined {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerIfExists(layerPath);
  }

  /**
   * Returns the first GeoView layer registered under the given root id.
   *
   * A match is a layer whose path is exactly the root id or is nested under it (`${rootId}/...`). This is useful when
   * only the root id is known (e.g. a GeoCore UUID whose sublayer path is resolved at runtime).
   *
   * @param rootId - The root layer id (e.g. a GeoCore UUID)
   * @returns The first AbstractBaseGVLayer under that root id
   * @throws {LayerNotFoundError} When no layer is found under the given root id
   */
  getGeoviewLayerByRootId(rootId: string): AbstractBaseGVLayer {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerByRootId(rootId);
  }

  /**
   * Returns the first GeoView layer registered under the given root id, if one exists.
   *
   * A match is a layer whose path is exactly the root id or is nested under it (`${rootId}/...`). This is useful when
   * only the root id is known (e.g. a GeoCore UUID whose sublayer path is resolved at runtime).
   *
   * @param rootId - The root layer id (e.g. a GeoCore UUID)
   * @returns The first AbstractBaseGVLayer under that root id, or undefined when none is found
   */
  getGeoviewLayerByRootIdIfExists(rootId: string): AbstractBaseGVLayer | undefined {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerByRootIdIfExists(rootId);
  }

  /**
   * Returns the AbstractGVLayer instance associated to the layer path.
   *
   * This returns an actual AbstractGVLayer and throws a LayerWrongTypeError if the layerPath points to a GVGroupLayer object.
   * An AbstractGVLayer is essentially a layer that's not a group layer.
   *
   * @param layerPath - The layer path
   * @returns The AbstractGVLayer Layer
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path
   */
  getGeoviewLayerRegular(layerPath: string): AbstractGVLayer {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerRegular(layerPath);
  }

  /**
   * Returns the GeoView Layer instance associated to the layer path, if it exists.
   *
   * This returns an actual AbstractGVLayer (or undefined) and throws a LayerWrongTypeError if the layerPath points to a GVGroupLayer object.
   * An AbstractGVLayer is essentially a layer that's not a group layer.
   *
   * @param layerPath - The layer path
   * @returns The AbstractGVLayer or undefined when not found
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path
   */
  getGeoviewLayerRegularIfExists(layerPath: string): AbstractGVLayer | undefined {
    // Retrieve from the domain
    return this.#layerDomain.getGeoviewLayerRegularIfExists(layerPath);
  }

  /**
   * Asynchronously returns the OpenLayer layer associated to a specific layer path.
   *
   * Resolves immediately if the layer is already registered; otherwise waits for the next
   * layer-registered event matching the given path.
   * Note this function uses the 'Async' suffix to differentiate it from 'getOLLayer'.
   *
   * @param layerPath - The layer path to the layer's configuration
   * @returns A promise that resolves to an OpenLayer layer associated to the layer path
   * @deprecated This method is deprecated and will be removed in future versions. Use `getGeoviewLayerRegular(layerPath).getOLLayer()` instead and
   * make the waiting asynchronicity happening here more clear in the caller, as it's a bit risky (layer might never be registered).
   */
  getOLLayerAsync(layerPath: string): Promise<BaseLayer> {
    // Retrieve from the domain
    return this.#layerDomain.getOLLayerAsync(layerPath);
  }

  // #endregion PUBLIC METHODS - DOMAIN SIMPLE GETTERS

  // #region PUBLIC METHODS - UI RELATED

  /**
   * Gets the ordered layer paths.
   *
   * @returns The ordered layer paths
   * @deprecated This method doesn't seem to be used anymore, remove?
   */
  getMapLayerOrderPaths(): string[] {
    // Retrieve from the store
    return getStoreLayerOrderedLayerPaths(this.getMapId());
  }

  /**
   * Sets the layer panel display state.
   *
   * @param displayState - The new display state for the layers view
   */
  setLayerDisplayState(displayState: TypeLayersViewDisplayState): void {
    // Save in the store
    setStoreLayerDisplayState(this.getMapId(), displayState);
  }

  /**
   * Sets the selected layer path in the layers tab.
   *
   * @param layerPath - The layer path to select
   */
  setSelectedLayerPath(layerPath: string): void {
    // Save in the store
    setStoreLayerSelectedLayersTabLayer(this.getMapId(), layerPath);
  }

  // #endregion PUBLIC METHODS - UI RELATED

  // #region PUBLIC METHODS

  /**
   * Checks if the specified layer is a raster layer.
   *
   * @param layerPath - The layer path to check
   * @returns True if the layer is a raster layer, false otherwise
   */
  isRasterLayer(layerPath: string): boolean {
    const layer = this.getGeoviewLayerRegularIfExists(layerPath);
    return layer instanceof AbstractGVRaster;
  }

  /**
   * Gets the max extent of all layers on the map, or of a provided subset of layers.
   *
   * Waits for each layer's bounds to be initialized before computing the union.
   *
   * @param layerIds - Identifiers or layerPaths of layers to get max extents from
   * @returns A promise that resolves with the overall extent or undefined when no bounds are found
   */
  getExtentOfMultipleLayers(layerIds: string[] = this.getLayerEntryLayerPaths()): Promise<Extent | undefined> {
    // Retrieve from the domain
    return this.#layerDomain.getExtentOfMultipleLayers(layerIds);
  }

  /**
   * Gets the extent of a feature or group of features.
   *
   * @param layerPath - The layer path
   * @param objectIds - The IDs of features to get extents from
   * @param outfield - Optional ID field to return for services that require a value in outfields
   * @returns A promise that resolves with the extent of the features
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   * @throws {NotImplementedError} When the underlying layer type does not implement extent-from-features (propagated from `getExtentFromFeatures()`)
   */
  getExtentFromFeatures(layerPath: string, objectIds: number[], outfield?: string): Promise<Extent> {
    // Get extent from features calling the GV Layer method
    return this.getGeoviewLayerRegular(layerPath).getExtentFromFeatures(objectIds, this.getMapViewer().getProjection(), outfield);
  }

  /**
   * Zooms to the full extent of a layer.
   *
   * @param layerPath - The path of the layer to zoom to
   * @param useAnimation - Optional flag indicating if a zoom animation should be used
   * @param fitOptions - Optional fit options to configure the zoom
   * @returns A promise that resolves when the zoom animation is complete
   * @throws {NoBoundsError} When the layer doesn't have bounds
   */
  zoomToLayerExtent(layerPath: string, useAnimation = true, fitOptions?: GVFitOptions): Promise<void> {
    // Get the layer bounds
    const bounds = getStoreLayerBounds(this.getMapId(), layerPath);

    // If found
    if (bounds) {
      return this.getControllersRegistry().mapController.zoomToExtent(bounds, useAnimation, fitOptions);
    }

    // Failed
    throw new NoBoundsError(layerPath);
  }

  /**
   * Zooms to the extent of an arbitrary set of feature info entries.
   *
   * Unions the extent already embedded in each entry when present, and falls back to looking up
   * the extent by object id for entries that don't carry one (e.g. ESRI Dynamic rows).
   *
   * @param layerPath - The layer path
   * @param features - The feature info entries to zoom to
   * @returns A promise that resolves when the zoom animation is complete
   * @throws {NotImplementedError} When the layer type does not implement extent-from-features (propagated from `getExtentFromFeatures()`)
   * @throws {NoExtentError} When no extent could be determined from the provided features
   */
  async zoomToFeaturesExtent(layerPath: string, features: TypeFeatureInfoEntry[]): Promise<void> {
    let extent: Extent | undefined;
    const objectIdsNeedingLookup: number[] = [];
    let oidField: string | undefined;

    features.forEach((feature) => {
      if (feature.extent) {
        extent = GeoUtilities.getExtentUnion(extent, feature.extent);
        return;
      }

      // Determine the oid field once, from the first feature that has one
      if (!oidField) oidField = Object.keys(feature.fieldInfo).find((key) => feature.fieldInfo[key]?.dataType === 'oid');
      if (oidField && feature.fieldInfo[oidField]) {
        objectIdsNeedingLookup.push(feature.fieldInfo[oidField]!.value as number);
      }
    });

    // Look up the extent for features that didn't already carry one
    if (objectIdsNeedingLookup.length > 0 && oidField) {
      const lookedUpExtent = await this.getExtentFromFeatures(layerPath, objectIdsNeedingLookup, oidField);
      extent = GeoUtilities.getExtentUnion(extent, lookedUpExtent);
    }

    // If no extent could be determined
    if (!extent) throw new NoExtentError(layerPath);

    // Zoom to it
    await this.getControllersRegistry().mapController.zoomToExtent(extent);
  }

  /**
   * Zooms to the specified extent, clamping the zoom level to the layer's visible scale range.
   *
   * Uses the layer's buffered effective scale boundaries so the resulting zoom remains inside
   * the layer's visibility range without landing exactly on service threshold values.
   *
   * @param layerPath - The layer path used to look up scale limits
   * @param extent - The extent to zoom to (in current map projection)
   * @param useAnimation - Optional flag indicating if a zoom animation should be used
   * @param fitOptions - Optional fit options to merge scale constraints into
   * @returns A promise that resolves when the zoom animation is complete
   */
  zoomToExtentRestricted(layerPath: string, extent: Extent, useAnimation = true, fitOptions?: GVFitOptions): Promise<void> {
    // Resolve the effective, buffered scale limits so zooming remains within the layer's usable visibility range.
    const { maxScaleZoomAt, minScaleZoomAt } = MapViewer.computeEffectiveLayerScales(
      this.getMapViewer(),
      this.getLayerEntryConfig(layerPath)
    );

    // Convert the scale limits to resolutions because OpenLayers applies fit constraints in resolution units.
    const theFitOptions: GVFitOptions = { ...(fitOptions ?? {}) };
    if (maxScaleZoomAt) {
      const minResolution = this.getControllersRegistry().mapController.getResolutionFromScale(maxScaleZoomAt);
      if (minResolution !== undefined) {
        // Keep both caller and layer constraints, choosing the more restrictive minimum resolution.
        theFitOptions.minResolution = Math.max(theFitOptions.minResolution ?? minResolution, minResolution);
      }
    }

    const mapSize = this.getMapViewer().map.getSize();
    const maxResolution = minScaleZoomAt ? this.getControllersRegistry().mapController.getResolutionFromScale(minScaleZoomAt) : undefined;
    if (mapSize && maxResolution) {
      // Reproduce the fit padding when deciding whether the requested extent would exceed the zoomed-out limit.
      const [width, height] = mapSize;
      const percentPadding = theFitOptions.percentPadding ?? OL_ZOOM_PERCENT_PADDING;
      const padding = theFitOptions.padding ?? [
        Math.round(height * percentPadding[1]),
        Math.round(width * percentPadding[0]),
        Math.round(height * percentPadding[1]),
        Math.round(width * percentPadding[0]),
      ];
      const paddedSize: [number, number] = [Math.max(1, width - padding[1] - padding[3]), Math.max(1, height - padding[0] - padding[2])];
      const fitResolution = this.getMapViewer().getView().getResolutionForExtent(extent, paddedSize);

      if (fitResolution > maxResolution) {
        // A normal fit would zoom out too far, so animate directly to the maximum allowed resolution instead.
        const center = getCenter(extent);
        const duration = useAnimation ? (theFitOptions.duration ?? OL_ZOOM_DURATION) : 0;
        return new Promise<void>((resolve) => {
          this.getMapViewer()
            .getView()
            .animate({ center, resolution: maxResolution, duration }, (complete) => {
              // Wait for the render after the view animation so callers observe a fully applied map state.
              this.getMapViewer()
                .waitForRender()
                .then(() => {
                  theFitOptions.callback?.(complete);
                  resolve();
                })
                .catch((error: unknown) => {
                  logger.logPromiseFailed('waitForRender in zoomToExtentRestricted maxResolution clamp', error);
                });
            });
        });
      }
    }

    // Zoom to extent and wait for it to finish
    return this.getControllersRegistry().mapController.zoomToExtent(extent, useAnimation, theFitOptions);
  }

  /**
   * Zooms to a feature's extent clamped to the layer's visible scale range, then highlights the feature.
   *
   * After the zoom completes, adds a click marker at the feature center, highlights the bounding box
   * with a fade-out effect, and sets the feature as the active highlight.
   *
   * @param layerPath - The layer path used to look up scale limits
   * @param feature - The feature info entry to zoom to and highlight
   * @param extent - The extent to restrictively zoom to
   * @param useAnimation - Optional flag indicating if a zoom animation should be used
   * @param fitOptions - Optional fit options to merge scale constraints into
   * @param isTemporaryHighlight - Optional flag indicating the feature highlight should expire automatically
   * @returns A promise that resolves when the zoom and highlight are complete
   */
  async zoomToExtentRestrictedAndHighlight(
    layerPath: string,
    feature: TypeFeatureInfoEntry,
    extent: Extent,
    useAnimation = true,
    fitOptions?: GVFitOptions,
    isTemporaryHighlight = false
  ): Promise<void> {
    // Get extent and center
    const center = getCenter(extent);

    // Transform the coordinate and use a state getter here, because we don't need to hook on value changes in this callback function.
    const lonlatCenter = Projection.transformToLonLat(
      center,
      Projection.getProjectionFromStringOrNumber(this.getMapViewer().getProjection())
    );

    // Zoom to extent and wait for it to finish
    await this.zoomToExtentRestricted(layerPath, extent, useAnimation, fitOptions);

    // Add a click marker, a bbox extent who will disappear and remove/add higlight the zoomed feature
    this.getControllersRegistry().mapController.clickMarkerIconShow({ lonlat: lonlatCenter });
    this.getControllersRegistry().mapController.highlightBBox(extent, false);
    this.getControllersRegistry().mapController.removeHighlightedFeature('all');
    this.getControllersRegistry().mapController.addHighlightedFeature(feature, isTemporaryHighlight);
  }

  /**
   * Gets the raster function previews for the ESRI image layer.
   *
   * @param layerPath - The layer path
   * @returns The raster function previews
   */
  getLayerRasterFunctionPreviews(layerPath: string): Map<string, Promise<string>> {
    const geoviewLayer = this.getGeoviewLayerIfExists(layerPath);
    if (!geoviewLayer || !(geoviewLayer instanceof GVEsriImage)) return new Map<string, Promise<string>>();

    return geoviewLayer.getRasterFunctionPreviews();
  }

  /**
   * Checks if a layer has a text layer.
   *
   * @param layerPath - The layer path of the layer to check
   * @returns True if the layer has a text layer, false otherwise
   */
  getLayerHasText(layerPath: string): boolean {
    const layer = this.getGeoviewLayerRegularIfExists(layerPath);

    // Check if it's a vector layer with a text layer
    if (layer instanceof AbstractGVVector) {
      return layer.getTextOLLayer() !== undefined;
    }

    return false;
  }

  /**
   * Gets the text visibility state for a layer.
   *
   * @param layerPath - The layer path of the layer to check
   * @returns True if text is visible, false otherwise. Returns undefined if layer has no text
   */
  getLayerTextVisibility(layerPath: string): boolean | undefined {
    const layer = this.getGeoviewLayerRegularIfExists(layerPath);

    // Check if it's a vector layer with a text layer
    if (layer instanceof AbstractGVVector && layer.getTextOLLayer()) {
      return layer.getTextVisible();
    }

    return undefined;
  }

  /**
   * Sets the text visibility for a layer.
   *
   * @param layerPath - The layer path of the layer to change
   * @param visible - True to show text, false to hide text
   */
  setLayerTextVisibility(layerPath: string, visible: boolean): void {
    // Get the layer
    const layer = this.getGeoviewLayerRegular(layerPath);
    if (!layer) return;

    // If it's a vector layer, set text visibility
    if (layer instanceof AbstractGVVector) {
      layer.setTextVisible(visible);

      // Update the store
      setStoreLayerTextVisibility(this.getMapId(), layerPath, visible);
    }
  }

  /**
   * Sets the name of the layer indicated by the given layer path.
   *
   * @param layerPath - The layer path to set the queryable property
   * @param name - The value to set for the name property
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   */
  setLayerName(layerPath: string, name: string): void {
    // Act on the domain
    this.getGeoviewLayer(layerPath).setLayerName(name);
  }

  /**
   * Sets opacity for a layer.
   *
   * @param layerPath - The path of the layer
   * @param opacity - The new opacity to use
   * @param emitOpacityChange - Whether to emit the event or not (false to avoid updating the legend layers)
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   */
  setLayerOpacity(layerPath: string, opacity: number, emitOpacityChange?: boolean): void {
    // Act on the domain
    this.getGeoviewLayer(layerPath).setOpacity(opacity, emitOpacityChange);
  }

  /**
   * Sets the queryable property of the layer indicated by the given layer path.
   *
   * @param layerPath - The layer path to set the queryable property
   * @param queryable - The value to set for the queryable property
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   * @throws {LayerNotQueryableError} When the underlying source is not queryable
   */
  setLayerQueryable(layerPath: string, queryable: boolean): void {
    // Act on the domain
    this.getGeoviewLayerRegular(layerPath).setQueryable(queryable);
  }

  /**
   * Sets hoverable state for a layer.
   *
   * @param layerPath - The path of the layer
   * @param hoverable - The new hoverable state for the layer
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   */
  setLayerHoverable(layerPath: string, hoverable: boolean): void {
    // Act on the domain
    this.getGeoviewLayerRegular(layerPath).setHoverable(hoverable);
  }

  /**
   * Sets or toggles the visibility of a layer within the current map.
   *
   * Retrieves the current visibility of the layer, determines the resulting visibility
   * based on the optional `newValue`, and applies the change only if the visibility
   * actually differs. If `newValue` is provided, the visibility is set explicitly;
   * if omitted, the method toggles the current visibility.
   *
   * @param layerPath - The path of the layer whose visibility is being updated
   * @param newValue - Optional. The new visibility value to apply. If omitted, the current visibility is toggled
   * @returns The resulting visibility state of the layer after the update
   * @throws {LayerNotFoundError} When the layer cannot be found at the given path
   */
  setOrToggleLayerVisibility(layerPath: string, newValue?: boolean): boolean {
    // Get the layer
    const gvLayer = this.getGeoviewLayer(layerPath);

    // Get current visibility
    const layerVisibility = gvLayer.getVisible();

    // Determine the outcome of the new visibility based on parameters
    const newVisibility = newValue !== undefined ? newValue : !layerVisibility;

    // Redirect to set or toggle the layer visibility and return the resulting visibility
    gvLayer.setVisible(newVisibility);

    // Return the new visibility
    return newVisibility;
  }

  /**
   * Makes a layer visible on the map, enabling any hidden parent groups so the layer actually renders.
   *
   * A child layer's own visibility has no visible effect while an ancestor group is hidden, so this walks
   * up the parent chain and turns each hidden ancestor visible before showing the layer itself.
   *
   * @param layerPath - The path of the layer to make visible
   * @throws {LayerNotFoundError} When the layer cannot be found at the given path (propagated from `getGeoviewLayer()`)
   */
  setLayerVisibleIncludingParents(layerPath: string): void {
    // Get the layer
    const gvLayer = this.getGeoviewLayer(layerPath);

    // Redirect to the layer, which enables hidden ancestor groups so the child isn't masked by a hidden parent
    gvLayer.setVisibleIncludingParents();
  }

  /**
   * Sets or toggles the visibility of a specific layer within a map.
   *
   * If the layer exists at the provided layer path for the given map, the method delegates
   * the visibility change to the map viewer's layer API. If `newValue` is provided, the layer
   * visibility is explicitly set to that value; otherwise, the visibility is toggled.
   *
   * @param layerPath - The path of the layer whose visibility is being changed
   * @param newValue - Optional. The new visibility value. If omitted, the visibility is toggled
   * @returns The resulting visibility state of the layer after the operation, or `false`
   * if the layer does not exist at the given path.
   * @deprecated Seems not used anymore by codebase (2026-04-20) - remove the function?
   */
  setOrToggleLayerVisibilityIfExists(layerPath: string, newValue?: boolean): boolean {
    // If the GV layer exists at the layer path
    if (this.getGeoviewLayerIfExists(layerPath)) {
      // Redirect to set or toggle the layer visibility and return the resulting visibility
      return this.setOrToggleLayerVisibility(layerPath, newValue);
    }
    return false;
  }

  /**
   * Sets the visibility of all GeoView layers on the map, including basemaps.
   *
   * Iterates through every registered GeoView layer and applies the provided
   * visibility value unconditionally via `setOrToggleLayerVisibility`.
   *
   * @param newValue - The visibility state to apply to all layers (`true` to show, `false` to hide)
   */
  setAllLayersVisibility(newValue: boolean): void {
    // For each layer
    this.getGeoviewLayers().forEach((layer) => {
      // There is a geoview layer at this layer path
      this.setOrToggleLayerVisibility(layer.getLayerPath(), newValue);
    });
  }

  /**
   * Sets the visibility of basemap layers only.
   *
   * Iterates through all GeoView layers and applies the provided visibility
   * value only to layers marked as basemaps (`useAsBasemap === true`). Layers
   * whose visibility already matches the desired state are skipped.
   *
   * @param newVisibility - The visibility state to apply to basemap layers (`true` to show, `false` to hide)
   */
  setAllLayersVisibilityBasemapsOnly(newVisibility: boolean): void {
    // For each layer
    this.getGeoviewLayers().forEach((layer) => {
      if (layer.getLayerConfig().getGeoviewLayerConfig().useAsBasemap && layer.getVisible() !== newVisibility) {
        this.setOrToggleLayerVisibility(layer.getLayerPath(), newVisibility);
      }
    });
  }

  /**
   * Sets the visibility of all non-basemap layers.
   *
   * Iterates through all GeoView layers and applies the provided visibility
   * value only to layers that are not marked as basemaps (`useAsBasemap !== true`).
   * Layers whose visibility already matches the desired state are skipped.
   *
   * @param newVisibility - The visibility state to apply to non-basemap layers (`true` to show, `false` to hide)
   */
  setAllLayersVisibilityExceptBasemaps(newVisibility: boolean): void {
    // For each layer
    this.getGeoviewLayers().forEach((layer) => {
      if (layer.getLayerConfig().getGeoviewLayerConfig().useAsBasemap !== true && layer.getVisible() !== newVisibility) {
        this.setOrToggleLayerVisibility(layer.getLayerPath(), newVisibility);
      }
    });
  }

  /**
   * Updates the visible-range settings (min/max zoom) of a GeoView layer and
   * stores whether the layer is currently within the visible range based on
   * the map's zoom level.
   *
   * Behavior:
   *  - Reads the layer's configuration to determine min/max zoom or min/max scale.
   *  - Converts scale-based limits into zoom levels when necessary.
   *  - Applies calculated `minZoom` and `maxZoom` to non-group layers only.
   *    (Group layers are skipped because their children already inherit the
   *     correct configuration and visibility is handled elsewhere.)
   *  - Computes whether the layer is currently in visible range and updates
   *    the store via `mapController`.
   *
   * @param gvLayer - The layer whose visibility
   *   range should be recalculated and stored.
   */
  updateLayerInVisibleRange(gvLayer: AbstractBaseGVLayer): void {
    // Get the layer config
    const mapViewer = this.getMapViewer();
    const view = mapViewer.getView();
    const effectiveScales = MapViewer.computeEffectiveLayerScales(mapViewer, gvLayer.getLayerConfig());

    // Set OL rendering limits via resolution (avoids zoom-level discretization).
    // Skip GVGroupLayers -> their children already carry their own limits, and locking the
    // group zoom/resolution would prevent children from loading when initially out of range.
    if (!(gvLayer instanceof GVGroupLayer)) {
      // Compute effective scales from the layer config, converting zoom-based limits to scale as needed
      const { maxScale, minScale } = effectiveScales;

      // maxScale -> OL minResolution: layer hidden when too zoomed in (resolution too small)
      const minResolution = maxScale ? mapViewer.getMapResolutionFromScale(maxScale) : undefined;
      if (minResolution) gvLayer.setMinResolution(minResolution);

      // minScale -> OL maxResolution: layer hidden when too zoomed out (resolution too large)
      const maxResolution = minScale ? mapViewer.getMapResolutionFromScale(minScale) : undefined;
      if (maxResolution) gvLayer.setMaxResolution(maxResolution);
    }

    // Determine the UI in-visible-range state from the OL resolution thresholds set above.
    // Using resolution directly matches OL rendering behavior and covers both scale-sourced
    // and zoom-sourced limits without any conversion.
    const currentResolution = view.getResolution() ?? view.getResolutionForZoom(view.getZoom() ?? 0);

    // Check if the layer falls in visible resolution range
    const inVisibleRange = currentResolution ? gvLayer.isInVisibleRange(currentResolution) : true;

    // Redirect
    this.setLayerInVisibleRange(gvLayer.getLayerPath(), inVisibleRange);

    // Continue with each parent of the layer if any
    let parentLayer = gvLayer.getParent();
    while (parentLayer) {
      // Check if the parent layer falls in visible resolution range
      const parentInVisibleRange = currentResolution ? parentLayer.isInVisibleRange(currentResolution) : true;

      // Redirect
      this.setLayerInVisibleRange(parentLayer.getLayerPath(), parentInVisibleRange);

      // Move up to the next parent
      parentLayer = parentLayer.getParent();
    }
  }

  /**
   * Sets the visible range state for a layer and updates Z indices.
   *
   * @param layerPath - The path identifying the target layer
   * @param inVisibleRange - Whether the layer is within the visible zoom range
   */
  setLayerInVisibleRange(layerPath: string, inVisibleRange: boolean): void {
    // Save to the store
    setStoreLayerInVisibleRange(this.getMapId(), layerPath, inVisibleRange);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Sets Z index for layers.
   */
  setLayerZIndices(): void {
    const reversedLayers = [...getStoreLayerOrderedLayerPaths(this.getMapId())].reverse();
    reversedLayers.forEach((layerPath, index) => {
      const gvLayer = this.getGeoviewLayerIfExists(layerPath);
      gvLayer?.setZIndex(index + 10);
    });
  }

  /**
   * Toggles the legend collapsed state for a layer in the store.
   *
   * @param layerPath - The path of the layer to toggle the legend collapsed state for
   */
  toggleLegendCollapsed(layerPath: string): void {
    // Get the current state
    const legendCollapsed = getStoreLayerLegendLayerByPath(this.getMapId(), layerPath)?.legendCollapsed ?? false;

    // Redirect
    this.setLegendCollapsed(layerPath, !legendCollapsed);
  }

  /**
   * Sets the legend collapsed state for a layer in the store.
   *
   * @param layerPath - The path of the layer to set the legend collapsed state for
   * @param legendCollapsed - The new collapsed state of the legend
   */
  setLegendCollapsed(layerPath: string, legendCollapsed: boolean): void {
    // Save in the store
    setStoreLayerLegendCollapsed(this.getMapId(), layerPath, legendCollapsed);
  }

  /**
   * Sets the legend collapsed state for all layers in the store.
   *
   * @param newCollapsed - The new collapsed state of the legends
   */
  setAllLayerCollapsed(newCollapsed: boolean): void {
    // Save in the store
    setStoreLayerAllMapLayerCollapsed(this.getMapId(), newCollapsed);
  }

  /**
   * Replaces a layer's paths in the ordered layers array.
   *
   * @param layerConfig - The config of the layer to add
   * @param layerPathToReplace - The layerPath of the entry to replace
   */
  replaceOrderedLayerPaths(layerConfig: ConfigBaseClass, layerPathToReplace?: string): void {
    const orderedLayers = [...getStoreLayerOrderedLayerPaths(this.getMapId())];
    const layerPath = layerConfig.getGeoviewLayerId() ? `${layerConfig.getGeoviewLayerId()}/base-group` : layerConfig.layerPath;
    const pathToSearch = layerPathToReplace || layerPath;
    const index = getStoreLayerOrderedLayerIndexByPath(this.getMapId(), pathToSearch);
    const replacedLayers = utilFindLayerAndChildrenPaths(pathToSearch, orderedLayers);

    const newLayerPaths = AbstractMapViewerController.generateOrderedLayerPaths(layerConfig);
    orderedLayers.splice(index, replacedLayers.length, ...newLayerPaths);

    // Save in the store
    setStoreLayerOrderedLayers(this.getMapId(), orderedLayers);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Adds a new layer to the ordered layers array using a layer config.
   *
   * @param geoviewLayerConfig - The config of the layer to add
   * @param index - Optional position to insert at
   */
  addOrderedLayerPathsByConfig(geoviewLayerConfig: TypeGeoviewLayerConfig | TypeLayerEntryConfig, index?: number): void {
    const orderedLayers = [...getStoreLayerOrderedLayerPaths(this.getMapId())];

    const newLayerPaths = AbstractMapViewerController.generateOrderedLayerPaths(geoviewLayerConfig);
    if (!index) orderedLayers.unshift(...newLayerPaths);
    else orderedLayers.splice(index, 0, ...newLayerPaths);

    // Save in the store
    setStoreLayerOrderedLayers(this.getMapId(), orderedLayers);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Adds a new layer path to the ordered layers array.
   *
   * @param layerPath - The layer path to add
   * @param index - Optional position to insert at
   */
  addOrderedLayerPath(layerPath: string, index?: number): void {
    const orderedLayers = [...getStoreLayerOrderedLayerPaths(this.getMapId())];
    if (!index) orderedLayers.unshift(layerPath);
    else orderedLayers.splice(index, 0, layerPath);

    // Save in store
    setStoreLayerOrderedLayers(this.getMapId(), orderedLayers);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Removes a layer from the ordered layers array.
   *
   * @param layerPath - The path of the layer to remove
   * @param removeSublayers - Should sublayers be removed
   */
  removeOrderedLayerPath(layerPath: string, removeSublayers = true): void {
    const orderedLayers = getStoreLayerOrderedLayerPaths(this.getMapId());
    const newOrderedLayers = removeSublayers
      ? orderedLayers.filter((path) => !path.startsWith(`${layerPath}/`) && path !== layerPath)
      : orderedLayers.filter((path) => path !== layerPath);

    // Save in store
    setStoreLayerOrderedLayers(this.getMapId(), newOrderedLayers);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Updates the ordered layers in the store and recalculates layer Z indices.
   *
   * @param orderedLayers - The new ordered layers array
   */
  setMapOrderedLayersDirectly(orderedLayers: string[]): void {
    // Save in store
    setStoreLayerOrderedLayers(this.getMapId(), orderedLayers);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Reorders a layer by moving it up or down in the layer stack.
   *
   * @param layerPath - The layer path to reorder
   * @param move - The number of positions to move (positive = up, negative = down)
   */
  reorderLayer(layerPath: string, move: number): void {
    // Redirect to state API
    this.getMapViewer().stateApi.reorderLayers(layerPath, move);
  }

  /**
   * Updates the raster function for an ESRI Image layer.
   *
   * @param layerPath - The path of the layer
   * @param rasterFunctionId - The raster function ID to apply or undefined to remove it
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is not an ESRI Image layer
   */
  setLayerRasterFunction(layerPath: string, rasterFunctionId: string | undefined): void {
    // Get the layer
    const layer = this.getGeoviewLayer(layerPath);

    // Check if it's the right type
    if (!(layer instanceof GVEsriImage)) throw new LayerWrongTypeError(layerPath, layer.getLayerName());

    // Update the raster function
    layer.setRasterFunction(rasterFunctionId);
  }

  /**
   * Sets the ascending flag on the mosaic rule for a layer.
   *
   * @param layerPath - The layer path
   * @param value - Whether the mosaic order is ascending
   */
  setLayerMosaicRuleAscending(layerPath: string, value: boolean): void {
    this.#setLayerMosaicRuleProperty(layerPath, { ascending: value });
  }

  /**
   * Sets the mosaic method on the mosaic rule for a layer.
   *
   * @param layerPath - The layer path
   * @param value - The mosaic method to set
   */
  setLayerMosaicRuleMethod(layerPath: string, value: TypeMosaicMethod): void {
    this.#setLayerMosaicRuleProperty(layerPath, { mosaicMethod: value });
  }

  /**
   * Sets the mosaic operation on the mosaic rule for a layer.
   *
   * @param layerPath - The layer path
   * @param value - The mosaic operation to set
   */
  setLayerMosaicRuleOperation(layerPath: string, value: TypeMosaicOperation): void {
    this.#setLayerMosaicRuleProperty(layerPath, { mosaicOperation: value });
  }

  /**
   * Sets the mosaic rule for an ESRI Image layer.
   *
   * @param layerPath - The layer path
   * @param mosaicRule - The mosaic rule to apply or undefined to remove it
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is not an ESRI Image layer
   */
  setLayerMosaicRule(layerPath: string, mosaicRule: TypeMosaicRule | undefined): void {
    // Get the layer
    const layer = this.getGeoviewLayer(layerPath);

    // Check if it's the right type
    if (!(layer instanceof GVEsriImage)) throw new LayerWrongTypeError(layerPath, layer.getLayerName());

    // Update the mosaic rule
    layer.setMosaicRule(mosaicRule);
  }

  /**
   * Checks if the layer results sets are all greater than or equal to the provided status.
   *
   * @param status - The layer status threshold to check against
   * @param callbackNotGood - Optional callback invoked for each layer that does not meet the status threshold
   * @returns Indicates if all layers passed the check and how many have passed
   */
  checkLayerStatus(status: TypeLayerStatus, callbackNotGood?: (layerConfig: ConfigBaseClass) => void): [boolean, number] {
    // Redirect to domain
    return this.#layerDomain.checkLayerStatus(status, callbackNotGood);
  }

  /**
   * Manually checks if all layers are loaded or error and update the store when so.
   *
   * @returns True if all layers are loaded
   */
  checkIfAllLayersLoadedAndUpdateStore(): boolean {
    // Get if all layers are loaded or error
    const [allLoadedOrError] = this.checkLayerStatus('loaded');

    // If all loaded/error
    if (allLoadedOrError) {
      // Update the store that all layers are loaded at this point
      setStoreLayersAreLoading(this.getMapId(), false);
    }

    // Return result
    return allLoadedOrError;
  }

  /**
   * Loops through all geoview layers and refresh their respective source.
   * Use this function on projection change or other viewer modification who may affect rendering.
   */
  refreshLayers(): void {
    // For each geoview layer
    this.getGeoviewLayers().forEach((geoviewLayer) => {
      // Recalculate the bounds
      LayerController.initBoundsForLayerAndParentsAndForget(
        this.getMapId(),
        geoviewLayer,
        this.getMapViewer().getProjection(),
        MapViewer.DEFAULT_STOPS
      );

      // Call the layer refresh function
      geoviewLayer.refresh(this.getMapViewer().getProjection());
    });
  }

  /**
   * Refreshes a layer and resets its states to their original configuration.
   *
   * This method performs the following steps:
   * 1. Retrieves the layer using the MapViewerLayer API.
   * 2. Calls the layer's `refresh` method to reload or redraw its data.
   * 3. Resets the layer's opacity and visibility to the values defined in its
   *    initial settings (defaulting to 1 for opacity and true for visibility).
   * 4. Updates all legend items' visibility if the layer is set to visible.
   *
   * @param layerPath - The layer path to refresh
   * @returns A promise that resolves once the layer has been refreshed,
   * its states reset, and its items rendered if visible
   * @throws {LayerNotFoundError} When the layer could not be found at the specified layer path
   */
  resetLayer(layerPath: string): Promise<void> {
    // Get the layer through layer API
    const layer = this.getGeoviewLayer(layerPath);

    // Refresh the layer
    layer.refresh(this.getMapViewer().getProjection());

    // Get the layer config
    const layerConfig = layer.getLayerConfig();

    // Reset layer states to original values
    const opacity = layerConfig.getInitialSettings()?.states?.opacity ?? 1; // default: 1
    const visibility = layerConfig.getInitialSettings()?.states?.visible ?? true; // default: true
    this.setLayerOpacity(layerPath, opacity);
    this.setOrToggleLayerVisibility(layerPath, visibility);

    if (visibility) {
      // Return the promise that all items visibility will be renderered if layer is set to visible
      return this.setAllItemsVisibility(layerPath, visibility, true);
    }

    // Resolve right away
    return Promise.resolve();
  }

  /**
   * Sets the visibility of a single legend item on a regular (non-group) layer.
   *
   * This method updates the visibility of the specified item both in the underlying
   * layer's style configuration and optionally in the legend store. It can also
   * trigger the layer filters to be reapplied and optionally wait for the next
   * render cycle before resolving. Finally, it emits an event indicating the visibility
   * change.
   *
   * @param layerPath - The path identifying the target layer within the map
   * @param item - The legend item whose visibility will be updated
   * @param visible - Whether the item should be visible
   * @param waitForRender - If `true`, the promise resolves only after the
   * underlying layer has finished its next render cycle.
   * @returns A promise that resolves once the visibility has been applied,
   * optional legend store updated, filters applied, and render completed if requested
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   * @throws {LayerStyleGeometryNotFoundError} When the geometry type of the item doesn't match any geometry type in the layer style configuration
   */
  setItemVisibility(layerPath: string, item: TypeLegendItem, visible: boolean, waitForRender: boolean): Promise<void> {
    // Get registered layer and set visibility
    return this.getGeoviewLayerRegular(layerPath).setStyleItemVisibility(item, visible, waitForRender);
  }

  /**
   * Toggles the visibility of a legend item on a specific layer.
   *
   * Inverts the current visibility of the given item and updates the corresponding layer.
   * Delegates to the layer API and can optionally wait for the layer to finish rendering.
   *
   * @param layerPath - The layer path
   * @param item - The legend item whose visibility will be toggled
   * @param waitForRender - If true, the returned promise resolves only after the layer has completed its next render cycle
   * @returns A promise that resolves once the visibility change has been applied
   * @throws {LayerStyleGeometryNotFoundError} When the geometry type of the item doesn't match any geometry type in the layer style configuration (propagated from `setItemVisibility()`)
   */
  toggleItemVisibility(layerPath: string, item: TypeLegendItem, waitForRender: boolean): Promise<void> {
    // Redirect to controller
    return this.setItemVisibility(layerPath, item, !item.isVisible, waitForRender);
  }

  /**
   * Toggles the visibility of a legend item without waiting for the render to complete.
   *
   * @param layerPath - The layer path
   * @param item - The legend item whose visibility will be toggled
   */
  toggleItemVisibilityAndForget(layerPath: string, item: TypeLegendItem): void {
    // Redirect
    this.toggleItemVisibility(layerPath, item, false).catch((error: unknown) => {
      // Log promise failed
      logger.logPromiseFailed('in LayerController.toggleItemVisibilityAndForget', error);
    });
  }

  /**
   * Sets the visibility of all legend items in a specific layer and optionally waits for rendering.
   *
   * This method performs the following steps:
   * 1. Ensures the layer itself is visible on the map.
   * 2. Updates the visibility of each item in the legend layer store and on the map.
   * 3. Triggers a re-render of the layer.
   * 4. Optionally waits for the next render cycle to complete before resolving.
   *
   * @param layerPath - The layer path
   * @param visibility - Whether all items in the layer should be visible
   * @param waitForRender - If true, the returned promise resolves only after the layer has completed its next render cycle
   * @returns A promise that resolves once all item visibilities have been updated and the layer has rendered if requested
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer was of wrong type
   * @throws {LayerStyleGeometryNotFoundError} When the geometry type of an item doesn't match any geometry type in the layer style configuration (propagated from `setItemVisibility()`)
   */
  async setAllItemsVisibility(layerPath: string, visible: boolean, waitForRender: boolean): Promise<void> {
    // Get the layer
    const layer = this.getGeoviewLayerRegular(layerPath);

    // Delegate the batch style mutation to the domain/layer and keep controller responsibilities
    // limited to store synchronization and controller-level event emission.
    const itemsToggled = await this.#layerDomain.setAllLayerItemsVisibility(layerPath, visible, waitForRender);

    // Update the store using the changed items returned by the domain batch.
    itemsToggled.forEach((item) => {
      // Save in the store
      setStoreLayerItemVisibility(this.getMapId(), layerPath, item, visible, layer.getLayerFilters().getClassFilter());

      // Emit event for each item visibility changed
      this.#emitLayerItemVisibilityChanged({ layer, item, visible });
    });
  }

  /**
   * Sets the visibility of all legend items without waiting for the render to complete.
   *
   * @param layerPath - The layer path
   * @param visibility - Whether all items should be visible
   */
  setAllItemsVisibilityAndForget(layerPath: string, visibility: boolean): void {
    // Redirect
    this.setAllItemsVisibility(layerPath, visibility, false).catch((error: unknown) => {
      // Log promise failed
      logger.logPromiseFailed('in LayerController.setAllItemsVisibilityAndForget', error);
    });
  }

  /**
   * Sets the highlighted layer state.
   *
   * Toggles or changes the highlighted layer. Only one layer can be highlighted at a time.
   *
   * @param layerPath - The layer path to set as the highlighted layer
   */
  setHighlightLayer(layerPath: string): void {
    // Get highlighted layer to set active button state because there can only be one highlighted layer at a time.
    const currentHighlight = getStoreLayerHighlightedLayer(this.getMapId());

    // Highlight layer and get new highlighted layer path from map controller.
    const highlightedLayerpath = this.changeOrRemoveLayerHighlight(layerPath, currentHighlight);

    // Save to the store
    setStoreLayerHighlightedLayer(this.getMapId(), highlightedLayerpath);
  }

  /**
   * Highlights layer or sublayer on map.
   *
   * @param layerPath - Identifier of layer to highlight
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   */
  highlightLayer(layerPath: string): void {
    // Clear previous highlights if any
    this.removeHighlightLayer();

    // Find the layer
    const layer = this.getGeoviewLayer(layerPath);

    // Store and set the highlighted layer path
    this.#highlightedLayerPath = layerPath;

    // Store ALL layers' opacities before any modifications.
    // We must capture the entire map state because:
    // 1. Ancestor groups must be boosted (so the highlighted layer isn't capped by parent opacity)
    // 2. Boosting ancestors cascades to siblings, changing their opacities
    // 3. Other layers will be dimmed by the ratio
    this.#highlightedLayerOriginalOpacity.clear();
    this.#storeAllLayerOpacities();

    // Get the ancestor groups of the highlighted layer (immediate parent -> root).
    // They must be boosted to 1.0 so the highlighted layer isn't capped.
    const ancestors = layer.getParents();

    // Boost ancestors from root down (reverse of getParents() order which is child -> root).
    // Each setOpacity(1) cascades to children, but that's fine - we'll override afterward.
    for (let i = ancestors.length - 1; i >= 0; i--) {
      ancestors[i].setOpacity(1);
    }

    // Boost the highlighted layer to 100% opacity.
    // For groups, setOpacity cascades to all children automatically.
    layer.setOpacity(1);

    // Build the set of layers that should NOT be dimmed:
    // the highlighted layer + its descendants (if group) + its ancestor groups
    const protectedLayers = new Set<AbstractBaseGVLayer>([layer, ...ancestors]);
    if (layer instanceof GVGroupLayer) {
      layer.getLayersAll().forEach((descendant) => protectedLayers.add(descendant));
    }

    // Dim all other leaf layers by the ratio.
    // Ancestor groups are already at 1.0 (needed to uncap children), so we only dim leaf layers.
    this.getGeoviewLayersRegulars()
      .filter((otherLayer) => !protectedLayers.has(otherLayer))
      .forEach((otherLayer) => {
        // Reduce the opacity on the other layer using the ratio.
        // Use the stored original opacity (not current, which may have been altered by ancestor cascade).
        const originalOpacity = this.#highlightedLayerOriginalOpacity.get(otherLayer.getLayerPath()) ?? otherLayer.getOpacity();
        otherLayer.setOpacity(originalOpacity / LayerController.HIGHLIGHT_OPACITY_RATIO);
      });
  }

  /**
   * Updates or removes the layer highlight.
   *
   * @param layerPath - The layer path to set as the highlighted layer
   * @param highlightedLayerPath - The layer path of the currently highlighted layer
   * @returns The layer path of the highlighted layer
   */
  changeOrRemoveLayerHighlight(layerPath: string, highlightedLayerPath: string): string {
    // If layer is currently highlighted layer, remove highlight
    if (highlightedLayerPath === layerPath) {
      this.setHighlightLayer('');
      this.removeHighlightLayer();
      return '';
    }

    // If the layer path is set to nothing, done
    if (!layerPath) return '';

    // Redirect to layer to highlight
    this.highlightLayer(layerPath);

    // Get bounds and highlight a bounding box for the layer (if true in global settings)
    const bounds = getStoreLayerBounds(this.getMapId(), layerPath);
    if (bounds && getStoreAppShowLayerHighlightLayerBbox(this.getMapId()))
      this.getControllersRegistry().mapController.highlightBBox(bounds, true);

    return layerPath;
  }

  /**
   * Removes layer and feature highlights for a given layer.
   *
   * @param layerPath - The path of the layer to remove highlights from
   */
  removeLayerHighlights(layerPath: string): void {
    // Remove layer highlight if layer being removed or its child is highlighted
    if (this.#highlightedLayerPath === layerPath) this.removeHighlightLayer();

    // Reset the result set for the layer and any children
    this.getLayerEntryLayerPaths().forEach((registeredLayerPath) => {
      if (registeredLayerPath.startsWith(`${layerPath}/`) || registeredLayerPath === layerPath) {
        // Remove feature highlight and result set for features from this layer
        this.getControllersRegistry().layerSetController.resetResultSet(registeredLayerPath);
      }
    });
  }

  /**
   * Removes layer or sublayer highlight.
   */
  removeHighlightLayer(): void {
    // Call map controller
    this.getControllersRegistry().mapController.removeBBoxHighlight();

    // Get the highlighted layer information
    const layer = this.getGeoviewLayerIfExists(this.#highlightedLayerPath);

    // If no current highlight, skip
    if (!layer) return;

    // Restore ALL layers to their original opacities.
    // Process top-level layers first (getGeoviewLayers returns groups + regulars at root level).
    // For groups, setOpacity cascades to children, then children get overridden by their own stored values.
    this.getGeoviewLayers().forEach((topLayer) => {
      this.#restoreLayerOpacity(topLayer);
      // If it's a group, restore all descendants top-down (getLayersAll is depth-first: parent before children)
      if (topLayer instanceof GVGroupLayer) {
        topLayer.getLayersAll().forEach((descendant) => {
          this.#restoreLayerOpacity(descendant);
        });
      }
    });
    this.#highlightedLayerOriginalOpacity.clear();

    // Clear the highlighted layer information
    this.#highlightedLayerPath = '';
  }

  /**
   * Stores the current opacity of all layers (groups and leaves) on the map into the opacity map.
   */
  #storeAllLayerOpacities(): void {
    this.getGeoviewLayers().forEach((topLayer) => {
      this.#highlightedLayerOriginalOpacity.set(topLayer.getLayerPath(), topLayer.getOpacity());
      if (topLayer instanceof GVGroupLayer) {
        topLayer.getLayersAll().forEach((descendant) => {
          this.#highlightedLayerOriginalOpacity.set(descendant.getLayerPath(), descendant.getOpacity());
        });
      }
    });
  }

  /**
   * Restores a single layer's opacity from the stored original opacity map.
   *
   * @param layer - The layer to restore opacity for
   */
  #restoreLayerOpacity(layer: AbstractBaseGVLayer): void {
    const originalOpacity = this.#highlightedLayerOriginalOpacity.get(layer.getLayerPath());
    if (originalOpacity !== undefined) {
      layer.setOpacity(originalOpacity);
    }
  }

  /**
   * Zooms to layer visible scale.
   *
   * Animates the view to a resolution that brings the layer within its visible range.
   * Uses resolution directly instead of converting through zoom level to avoid discretization.
   *
   * @param layerPath - Path of layer to zoom to
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   */
  zoomToLayerVisibleScale(layerPath: string): void {
    const mapViewer = this.getMapViewer();
    const view = mapViewer.getView();
    const mapZoom = view.getZoom();
    const currentScale = mapZoom !== undefined ? mapViewer.getMapScaleFromZoom(mapZoom) : undefined;
    const geoviewLayer = this.getGeoviewLayer(layerPath);

    // Compute effective scales from the layer config, converting zoom-based limits to scale as needed
    const { maxScaleZoomAt, minScaleZoomAt } = MapViewer.computeEffectiveLayerScales(mapViewer, geoviewLayer.getLayerConfig());

    // Set the target scale that brings the current view inside the visible range.
    // minScale is the zoomed-out limit (larger denominator), maxScale is the zoomed-in limit (smaller denominator).
    let targetScale: number | undefined;
    if (currentScale && maxScaleZoomAt && currentScale < maxScaleZoomAt) {
      // Too zoomed in, zoom out to the maxScale boundary.
      targetScale = maxScaleZoomAt;
    } else if (currentScale && minScaleZoomAt && currentScale > minScaleZoomAt) {
      // Too zoomed out, zoom in to the minScale boundary.
      targetScale = minScaleZoomAt;
    }

    // Convert target scale directly to resolution (more direct than scale -> zoom -> resolution).
    const targetResolution = targetScale ? mapViewer.getMapResolutionFromScale(targetScale) : undefined;
    if (targetResolution === undefined) return;

    // Get the bounds as stored in the layer
    const layerExtent = geoviewLayer.getBounds();

    // Animate view to the target resolution centered on the layer extent if applicable.
    // If there is no layerExtent or if the zoom needs to zoom out, the center will be undefined and not change.
    // Check if the map center is already in the layer extent and if so, do not recenter.
    const centerExtent =
      layerExtent &&
      currentScale &&
      targetScale &&
      targetScale < currentScale &&
      !GeoUtilities.isPointInExtent(view.getCenter()!, layerExtent)
        ? [(layerExtent[2] + layerExtent[0]) / 2, (layerExtent[1] + layerExtent[3]) / 2]
        : undefined;

    view.animate({
      center: centerExtent,
      resolution: targetResolution,
      duration: OL_ZOOM_DURATION,
    });
  }

  /**
   * Clears any overridden CRS settings on all WMS layers in the map.
   *
   * Iterates through all GeoView layers, identifies those that are instances of `GVWMS`,
   * and resets their override CRS to `undefined`, allowing them to use the default projection behavior.
   */
  clearWMSLayersWithOverrideCRS(): void {
    // Get all WMS layers
    const wmsLayers = this.getGeoviewLayers().filter((layer) => layer instanceof GVWMS);
    wmsLayers.forEach((gvLayer) => gvLayer.setOverrideCRS(undefined));
  }

  /**
   * Sets the date temporal mode for the specific layer.
   *
   * This updates the layer-level configuration used to control how date values
   * are interpreted.
   * The value is stored in the application state via the LayerController.
   *
   * @param layerPath - The unique path identifying the layer
   * @param temporalMode - The temporal mode to apply for interpreting date values associated with this layer
   */
  setLayerDateTemporalMode(layerPath: string, temporalMode: TemporalMode): void {
    // Redirect
    setStoreLayerDateTemporal(this.getMapId(), layerPath, temporalMode);
  }

  /**
   * Sets the date display format for a specific layer.
   *
   * This updates the layer-level configuration used to control how date values
   * are formatted when displayed (e.g., in legends, tooltips, or UI components).
   * The value is stored in the application state via the LayerController.
   *
   * @param layerPath - The unique path identifying the layer
   * @param displayDateFormat - The date format to apply for displaying date values associated with this layer
   */
  setLayerDisplayDateFormat(layerPath: string, displayDateFormat: TypeDisplayDateFormat | string): void {
    // Make sure of the input format
    let displayDateFormatToSet: TypeDisplayDateFormat = displayDateFormat as TypeDisplayDateFormat;
    if (typeof displayDateFormat === 'string') displayDateFormatToSet = { en: displayDateFormat, fr: displayDateFormat };

    // Save to the store
    setStoreLayerDisplayDateFormat(this.getMapId(), layerPath, displayDateFormatToSet);
  }

  /**
   * Sets the date display format (short) for a specific layer.
   *
   * Short means the date should be displayed in a more compact format.
   * This updates the layer-level configuration used to control how date values
   * are formatted when displayed (e.g., in legends, tooltips, or UI components).
   * The value is stored in the application state via the LayerController.
   *
   * @param layerPath - The unique path identifying the layer
   * @param displayDateFormat - The date format to apply for displaying date values associated with this layer
   */
  setLayerDisplayDateFormatShort(layerPath: string, displayDateFormat: TypeDisplayDateFormat | string): void {
    // Make sure of the input format
    let displayDateFormatToSet: TypeDisplayDateFormat = displayDateFormat as TypeDisplayDateFormat;
    if (typeof displayDateFormat === 'string') displayDateFormatToSet = { en: displayDateFormat, fr: displayDateFormat };

    // Save to the store
    setStoreLayerDisplayDateFormatShort(this.getMapId(), layerPath, displayDateFormatToSet);
  }

  /**
   * Sets the WMS style for a WMS layer.
   *
   * @param layerPath - The layer path
   * @param wmsStyleName - The WMS style name to apply
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is not a WMS layer
   */
  setLayerWmsStyle(layerPath: string, wmsStyleName: string | undefined): void {
    if (!wmsStyleName) return; // Skip if no style name to apply

    // Get the layer
    const layer = this.getGeoviewLayer(layerPath);

    // Check if it's the right type
    if (!(layer instanceof GVWMS)) throw new LayerWrongTypeError(layerPath, layer.getLayerName());

    // Update the WMS style
    layer.setWmsStyle(wmsStyleName);
  }

  /**
   * Changes a GeoJson Source of a GeoJSON layer at the given layer path.
   *
   * @param layerPath - The path of the layer
   * @param geojson - The new geoJSON
   * @returns A promise that resolves when the geojson source has been set
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path
   * @throws {LayerNotGeoJsonError} When the layer is not a GeoJson layer
   */
  async setGeojsonSource(layerPath: string, geojson: GeoJSONObject | string): Promise<void> {
    // Get the GeoviewLayer
    const gvLayer = this.getGeoviewLayerRegular(layerPath);

    // If not of right type
    if (!(gvLayer instanceof GVGeoJSON)) throw new LayerNotGeoJsonError(layerPath, gvLayer.getLayerName());

    // Override the GeoJson source
    await gvLayer.setGeojsonSource(geojson, this.getMapViewer().getProjection());

    // Reset the feature info result set
    this.getControllersRegistry().layerSetController.resetResultSet(layerPath);

    // Update feature info
    this.getControllersRegistry()
      .layerSetController.triggerGetAllFeatureInfo(layerPath)
      .catch((error: unknown) => {
        // Log
        logger.logPromiseFailed(`Update all feature info in setGeojsonSource failed for layer ${layerPath}`, error);
      });
  }

  /**
   * Queries the EsriDynamic layer at the given layer path for a specific set of object IDs.
   *
   * @param layerPath - The layer path of the layer to query
   * @param objectIDs - The object IDs to filter the query on
   * @returns A promise that resolves with an array of feature info entry records
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path
   * @throws {LayerNotEsriDynamicError} When the layer configuration isn't EsriDynamic
   */
  queryLayerEsriDynamic(layerPath: string, objectIDs: number[]): Promise<TypeFeatureInfoEntryPartial[]> {
    // Get the layer
    const layer = this.getGeoviewLayerRegular(layerPath);

    // If not EsriDynamic
    if (!(layer instanceof GVEsriDynamic)) throw new LayerNotEsriDynamicError(layerPath, layer.getLayerName());

    // Perform the query
    return layer.getRecordsByOIDs(objectIDs, this.getMapViewer().getProjectionNumber());
  }

  /**
   * Starts the delayed deletion process for a layer, allowing a short
   * time window for the user to undo the operation.
   *
   * During this period:
   * - The layer is temporarily hidden.
   * - A deletion start timestamp is stored so the UI can derive progress locally.
   * - The user may abort the deletion via {@link deleteLayerAbort}.
   *
   * If the undo window expires, the layer is permanently deleted.
   * If called again for the same layer while a previous timer is running,
   * the previous timer is cancelled and a new one starts, preserving the
   * original visibility state from the first call.
   *
   * @param layerPath - Unique path identifying the layer within the map
   * @param undoWindowDuration - Duration in milliseconds of the undo window before deletion is finalized
   * @returns A promise resolving to:
   * - `true` if the deletion completed successfully.
   * - `false` if the deletion was aborted, superseded by a newer call, or
   *   if the layer was already in the deletion process.
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path
   */
  async deleteLayerStartTimer(layerPath: string, undoWindowDuration: number): Promise<boolean> {
    // If there was already a job going, cancel it but keep the reference so we can preserve its original visibility
    const existingJob = this.#getLayerBeingDeleted(layerPath);
    if (existingJob) {
      // Cancel
      existingJob.delayedJob.cancel();
    }

    // Get the layer if it exists, it's possible it doesn't exist if the layer failed to process
    const gvLayer = this.getGeoviewLayerIfExists(layerPath);

    // Note the original visibility state of the layer before starting the deletion process.
    // If there was already a pending deletion, preserve its original visibility since the layer is already hidden.
    const originalVisibility = existingJob?.originalVisibility ?? gvLayer?.getVisible() ?? false;

    // Hide layer immediately
    gvLayer?.setVisible(false);
    this.removeLayerHighlights(layerPath);

    // Set start deletion time in the store
    setStoreLayerDeletionStartTime(this.getMapId(), layerPath, Date.now());

    // Start delayed job
    const delayedJob = doTimeout(undoWindowDuration);

    // Register job (replaces any previous entry for this layerPath)
    this.#addLayerBeingDeleted(layerPath, {
      delayedJob,
      originalVisibility,
    });

    // Wait for the job to perform operation or be cancelled
    const result = await delayedJob.promise;

    // Check if our job is still the current one. A subsequent call to deleteLayerStartTimer
    // may have replaced it while we were awaiting - in that case, let the newer call own the lifecycle.
    const currentJob = this.#getLayerBeingDeleted(layerPath);
    if (currentJob?.delayedJob !== delayedJob) {
      return false;
    }

    // Our job is still current - remove it from the stack
    this.#removeLayerBeingDeleted(layerPath);

    if (result === 'timeout') {
      // Delete layer through controller
      this.getControllersRegistry().layerCreatorController.removeLayerUsingPath(layerPath);
      return true;
    }

    // Undo deletion - restore original visibility
    gvLayer?.setVisible(originalVisibility);

    // Negative
    return false;
  }

  /**
   * Aborts an ongoing layer deletion process if it has not yet been finalized.
   *
   * This restores the layer to its previous visibility state and stops
   * the deletion timer.
   *
   * @param layerPath - Unique path identifying the layer within the map
   */
  deleteLayerAbort(layerPath: string): void {
    // Get the job about layer deletion
    const job = this.#getLayerBeingDeleted(layerPath);

    // Cancel the delayed job
    job?.delayedJob.cancel();
  }

  /**
   * Waits until all GeoView layers reach the specified status before resolving the promise.
   *
   * Event-driven: subscribes to layer status changes and re-checks the condition on each event.
   * Immune to background-tab timer throttling.
   *
   * Note: we don't need to subscribe to layer-entry-config-registered events. When a sub-layer
   * registers on-the-fly (notably: EsriDynamic, WMS), the LayerDomain attaches its status-change
   * handler at registration time, so every subsequent transition (toward loaded/error) of that
   * new sub-layer will fire onLayerStatusChanged here.
   *
   * @param layerStatus - The desired status to wait for (e.g., 'loaded', 'processed')
   * @returns A promise that resolves with the number of layers that have reached the specified status
   */
  async waitForAllLayersStatus(layerStatus: TypeLayerStatus): Promise<number> {
    // First, check synchronously — the condition may ALREADY be met
    const [allGoodNow, countNow] = this.checkLayerStatus(layerStatus);
    if (allGoodNow) return Promise.resolve(countNow);

    // Subscribe via onceLayerStatusChangedAsync with a filter that re-checks the global condition on each event
    await this.#layerDomain.onceLayerStatusChanged(() => {
      const [allGood] = this.checkLayerStatus(layerStatus, (layerConfig) => {
        // Log
        logger.logTraceDetailed(
          `waitForAllLayersStatus - waiting on layer to be '${layerStatus}'...`,
          layerConfig.layerPath,
          layerConfig.layerStatus
        );
      });
      return allGood;
    });

    // After the event fires, re-check the condition and return the count
    return this.checkLayerStatus(layerStatus)[1];
  }

  /**
   * Waits for a layer entry config to be registered and returns the config associated to a specific layer path.
   *
   * Resolves immediately if the config is already registered; otherwise subscribes to the `onceLayerEntryConfigRegistered` event and resolves as soon as a config with the matching path is registered.
   *
   * @param layerPath - The layer path to the layer's configuration
   * @param timeout - Optional timeout in milliseconds. When provided, the promise will reject if the config is not registered within the specified time
   * @returns A promise that resolves to the layer entry config associated to the layer path
   */
  waitForLayerConfigRegistered(layerPath: string, timeout?: number): Promise<ConfigBaseClass> {
    // Redirect
    return this.#layerDomain.waitForLayerConfigRegistered(layerPath, timeout);
  }

  /**
   * Waits for a layer to be registered and returns the GeoView layer associated to a specific layer path.
   *
   * Resolves immediately if the layer is already registered; otherwise subscribes to the `onceLayerRegistered` event and resolves as soon as a layer with the matching path is registered.
   *
   * @param layerPath - The layer path to the layer's configuration
   * @param timeout - Optional timeout in milliseconds. When provided, the promise will reject if the layer is not registered within the specified time
   * @returns A promise that resolves to a GeoView layer associated to the layer path
   */
  waitForLayerRegistered(layerPath: string, timeout?: number): Promise<AbstractBaseGVLayer> {
    // Redirect
    return this.#layerDomain.waitForLayerRegistered(layerPath, timeout);
  }

  /**
   * Waits for the map layers loaded event to be emitted.
   *
   * @returns A promise that resolves with the number of layers that have reached the specified status
   */
  async waitForLayersLoaded(): Promise<number> {
    // First, wait for the map to be ready in case it's not ready yet. We need the layer configs to be registered at least!
    await this.getMapViewer().waitForMapReady();

    // Redirect
    return this.waitForAllLayersStatus('loaded');
  }

  /**
   * Waits for all map layers to reach the loaded status while periodically forcing OpenLayers render cycles.
   *
   * In background tabs, browsers throttle or pause `requestAnimationFrame` callbacks. This starves
   * OpenLayers of render frames, which prevents:
   * - Tile and image sources from requesting data (blocking layer loaded status transitions).
   * - The `rendercomplete` event from firing (blocking any code that awaits full render completion).
   *
   * To work around this, the method calls `map.renderSync()` every 1 000 ms via `doUntilPromise`
   * until the layers-loaded promise resolves. This keeps the OL rendering pipeline alive so layers
   * can load regardless of tab visibility.
   *
   * @returns A promise that resolves with the number of layers that have reached the loaded status
   */
  waitForLayersLoadedForcingRenders(): Promise<number> {
    // Start waiting for all layers to reach the loaded status
    const waitPromise = this.waitForLayersLoaded();

    // Force a synchronous OL render every 1 000 ms until all layers are loaded
    doUntilPromise(
      (): void => {
        // Trigger an immediate, synchronous render frame on the OL map
        this.getMapViewer().map?.renderSync();
      },
      waitPromise,
      1000
    );

    return waitPromise;
  }

  // #endregion PUBLIC METHODS

  // #region MAP HANDLERS

  /**
   * Handles when the map layers have been processed (e.g., after initial load or a significant change).
   *
   * @param sender - The map viewer that fired the event
   * @param event - The event data (not used in this handler)
   */
  #handleMapLayersProcessed(sender: MapViewer, event: MapBaseEvent): void {
    // Update the layers visible in range
    this.#updateLayersVisibleInRange();
  }

  /**
   * Handles when the map has finished moving (e.g., after a pan or zoom action).
   *
   * @param sender - The map viewer that fired the event
   * @param event - The event data (not used in this handler)
   */
  #handleMapMoveEnd(sender: MapViewer, event: MapMoveEndEvent): void {
    // Update the layers visible in range
    this.#updateLayersVisibleInRange();
  }

  // #endregion MAP HANDLERS

  // #region DOMAIN HANDLERS
  // GV Eventually, these should be moved to a store adaptor or similar construct that directly connects the domain to the store without going through the controller
  // GV.CONT but for now this allows us to keep domain-store interactions in one place and call application-level processes as needed during migration.

  /**
   * Handles when a layer entry config is registered in the domain.
   *
   * Registers the layer for ordered layer info, notifies all layer sets,
   * and sets the layer status to registered.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the registered layer config
   */
  #handleDomainLayerEntryConfigRegistered(sender: LayerDomain, event: DomainLayerStatusChangedEvent): void {
    // Register for ordered layer information
    if (event.config.getGeoviewLayerConfig().useAsBasemap !== true) this.#registerForOrderedLayerInfo(event.config);

    // Tell the layer sets about it
    this.getControllersRegistry().layerSetController.allLayerSets.forEach((layerSet) => {
      // Register the config to the layer set
      layerSet.registerLayerConfig(event.config);
    });

    // Set the layer status to registered
    event.config.setLayerStatusRegistered();
  }

  /**
   * Handles when a layer entry config is unregistered from the domain.
   *
   * Notifies all layer sets to unregister the layer.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the unregistered layer config
   */
  #handleDomainLayerEntryConfigUnregistered(sender: LayerDomain, event: DomainLayerStatusChangedEvent): void {
    // GV Could be moved to layer-set-controller, but keeping it here for now to be next to the layer entry config registered event hook

    // Tell the layer sets about it
    this.getControllersRegistry().layerSetController.allLayerSets.forEach((layerSet) => {
      // Unregister from the layer set
      layerSet.unregister(event.config.layerPath);
    });
  }

  /**
   * Handles when a layer registered in the domain.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the registered layer
   */
  #handleDomainLayerRegistered(sender: LayerDomain, event: DomainLayerRegisteredEvent): void {
    // If a non-vector layer has been registered
    // GV Vector layers get their bounds set only when they are 'loaded'
    if (!(event.layer instanceof AbstractGVVector)) {
      // Calculate the bounds upon creation
      LayerController.initBoundsForLayerAndParentsAndForget(
        this.getMapId(),
        event.layer,
        this.getMapViewer().getProjection(),
        MapViewer.DEFAULT_STOPS
      );
    }
  }

  /**
   * Handles when a layer unregistered from the domain.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the unregistered layer
   */
  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  #handleDomainLayerUnregistered(sender: LayerDomain, event: DomainLayerRegisteredEvent): void {
    //
    // Do something here....
    //
  }

  /**
   * Handles when a layer name is changed in the domain.
   *
   * @param layer - The layer that's become changed
   * @param event - The event containing the name change
   */
  #handleDomainLayerNameChanged(sender: LayerDomain, event: DomainLayerNameChangedEvent): void {
    setStoreLayerName(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.layerName);
  }

  /**
   * Handles when a layer visibility is changed on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the visibility change
   */
  #handleDomainLayerVisibleChanged(sender: LayerDomain, event: DomainLayerVisibleChangedEvent): void {
    // Save to the store
    setStoreLayerVisibility(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.visible);

    // Update the z indices
    this.setLayerZIndices();
  }

  /**
   * Handles when a layer opacity is changed on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the opacity change
   */
  #handleDomainLayerOpacityChanged(sender: LayerDomain, event: DomainLayerOpacityChangedEvent): void {
    // Update the store
    setStoreLayerOpacity(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.opacity);
  }

  /**
   * Handles when a layer loading state is changed on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the loading state change
   */
  #handleDomainLayerLoadingChanged(sender: LayerDomain, event: DomainLayerBaseEvent): void {
    // Update the store that at least 1 layer is loading
    setStoreLayersAreLoading(this.getMapId(), true);
  }

  /**
   * Handles when a layer first loaded state is changed on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the first loaded state change
   */
  #handleDomainLayerFirstLoadedChanged(sender: LayerDomain, event: DomainLayerBaseEvent): void {
    // Set in visible range property for all newly added layers
    this.updateLayerInVisibleRange(event.layer);

    // If the layer is regular layer
    if (event.layer instanceof AbstractGVLayer) {
      // Register the layer in the time-slider if it must
      this.getControllersRegistry().timeSliderController?.tryRegisterLayer(event.layer);
    }

    // KML has no style/symbology; warn the user only once the KML layer has actually loaded
    if (event.layer instanceof GVKML) {
      this.getMapViewer().notifications.showWarning('warning.layer.kmlLayerWarning', { layerName: event.layer.getLayerName() });
    }
  }

  /**
   * Handles when a layer loaded state is changed on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the loaded state change
   */
  #handleDomainLayerLoadedChanged(sender: LayerDomain, event: DomainLayerBaseEvent): void {
    // If a vector layer has been loaded
    // GV Vector layers get their bounds set only when they are 'loaded'
    if (event.layer instanceof AbstractGVVector) {
      // Calculate the bounds as those depend on the actual features in the layer
      LayerController.initBoundsForLayerAndParentsAndForget(
        this.getMapId(),
        event.layer,
        this.getMapViewer().getProjection(),
        MapViewer.DEFAULT_STOPS
      );
    }
  }

  /**
   * Handles when all layers are loaded/error state on the map.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the loaded state change
   */
  #handleDomainLayerAllLoaded(sender: LayerDomain, event: DomainLayerStatusChangedEvent): void {
    // Update the store that all layers are loaded at this point
    setStoreLayersAreLoading(this.getMapId(), false);
  }

  /**
   * Handles layer-specific messages and displays them through the map viewer's notification system.
   *
   * @param sender - The layer domain that fired the event
   * @param layerMessageEvent - The message event containing notification details
   */
  #handleDomainLayerMessage(sender: LayerDomain, layerMessageEvent: DomainLayerMessageEvent): void {
    // Read event params for clarity
    const { messageType } = layerMessageEvent.layerEvent;
    const { messageKey } = layerMessageEvent.layerEvent;
    const { messageParams } = layerMessageEvent.layerEvent;
    const { notificationMessageKey } = layerMessageEvent.layerEvent;
    const { notificationMessageParams } = layerMessageEvent.layerEvent;

    // When a separate notification message is provided for an info progress update, show the detailed text in the
    // snackbar but group a generic message in the notification panel (avoids flooding the panel with one entry per
    // high-frequency update). This grouped path is info-only; warnings/errors fall through to the ungrouped show* below.
    if (notificationMessageKey && messageType === 'info') {
      this.getMapViewer().notifications.showInfoDetailedSnackbar(
        messageKey,
        messageParams ?? {},
        notificationMessageKey,
        notificationMessageParams
      );
      return;
    }

    if (messageType === 'info') {
      this.getMapViewer().notifications.showMessage(messageKey, messageParams);
    } else if (messageType === 'warning') {
      this.getMapViewer().notifications.showWarning(messageKey, messageParams);
    } else if (messageType === 'error') {
      this.getMapViewer().notifications.showError(messageKey, messageParams);
    } else if (messageType === 'success') {
      this.getMapViewer().notifications.showSuccess(messageKey, messageParams);
    }
  }

  /**
   * Handles when a layer's hoverable state changes in the domain.
   *
   * Propagates the change to the store and clears feature info results
   * when hoverable is turned off.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the layer and new hoverable state
   */
  #handleDomainLayerHoverableChanged(sender: LayerDomain, event: DomainLayerHoverableChangedEvent): void {
    // Save in store
    setStoreLayerHoverable(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.hoverable);

    // If not hoverable
    if (!event.layerEvent.hoverable) {
      // Clear the results when turning the hoverable to false
      this.getControllersRegistry().layerSetController.hoverFeatureInfoLayerSet.clearResults();
    }
  }

  /**
   * Handles when a layer's queryable state changes in the domain.
   *
   * Propagates the change to the store and clears feature info results
   * when queryable is turned off.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the layer and new queryable state
   */
  #handleDomainLayerQueryableChanged(sender: LayerDomain, event: DomainLayerQueryableChangedEvent): void {
    // Save in store
    setStoreLayerQueryable(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.queryable);

    // If not queryable
    if (!event.layerEvent.queryable) {
      // Clear the results from the layer set when turning the queryable to false
      this.getControllersRegistry().layerSetController.clearFeatureInfoLayerResults(event.layer.getLayerPath());
    }
  }

  /**
   * Handles when a layer's item visibility state changes in the domain.
   *
   * Propagates the change to the store and clears feature info results
   * when visibility is turned off.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the layer and new visibility state
   */
  #handleDomainLayerItemVisibilityChanged(sender: LayerDomain, event: DomainLayerItemVisibilityChangedEvent): void {
    // Get the layer path
    const layerPath = event.layer.getLayerPath();

    // Save to the store
    setStoreLayerItemVisibility(
      this.getMapId(),
      layerPath,
      event.layerEvent.item,
      event.layerEvent.visible,
      event.layer.getLayerFilters().getClassFilter()
    );

    // Emit event
    this.#emitLayerItemVisibilityChanged({ layer: event.layer, item: event.layerEvent.item, visible: event.layerEvent.visible });
  }

  /**
   * Handles when a layer's filter is applied in the domain.
   *
   * Propagates the change to the store.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The event containing the layer and filter information
   */
  #handleDomainLayerFilterApplied(sender: LayerDomain, event: DomainLayerFilterAppliedEvent): void {
    // TODO: REFACTOR FILTERS - Ideally, this handler wouldn't exist and the layer would have custom events when adjusting their data/time filters.
    // TO.DOCONT: It'd have to be a custom event, because it'd have to be more elaborate than a single query string if we
    // TO.DOCONT: want to update the UI based on a query filter that happened. Refer to setStyleItemVisibility for the
    // TO.DOCONT: correct pattern (which does update the UI automatically). Search id: 0ecb948f
    // Depending on the layer category
    switch (event.layerEvent.filterCategory) {
      case 'data':
        // Save in the store
        setStoreDataTableFilter(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.filter.getDataFilter());
        break;

      case 'time':
        // If the time slider is initialized
        if (isStoreTimeSliderInitialized(this.getMapId())) {
          // Save in the store
          setStoreTimeSliderFilter(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.filter.getTimeFilter());
        }
        break;
    }
  }

  /**
   * Handles the event triggered when a layer is added to a `GVGroupLayer`.
   *
   * When a layer is added to a group, this handler checks whether the added
   * layer is a concrete `AbstractGVLayer` (i.e., not another group wrapper type).
   * If so, it waits for the layer's source to become ready before recalculating
   * and propagating its bounds to the store. This ensures bounds are computed
   * only after the layer has sufficient metadata (e.g., extent) available.
   * Bounds propagation may also affect the parent group hierarchy.
   *
   * @param sender - The group layer receiving the new child layer
   * @param event - The event payload containing information about the layer addition
   */
  #handleDomainLayerGroupLayerAdded(sender: LayerDomain, event: DomainLayerGroupChildrenUpdatedEvent): void {
    // Calculate the bounds on the group layer which had a layer added
    LayerController.initBoundsForLayerAndParentsAndForget(
      this.getMapId(),
      event.layer,
      this.getMapViewer().getProjection(),
      MapViewer.DEFAULT_STOPS
    );

    // Get the initial settings opacity of the group layer
    const initialSettingsOpacity = event.layer.getLayerConfig().getInitialSettings()?.states?.opacity;

    // If any
    if (initialSettingsOpacity !== undefined) {
      // Update the store
      setStoreLayerOpacity(this.getMapId(), event.layer.getLayerPath(), initialSettingsOpacity);
    }
  }

  /**
   * Handles the event triggered when a layer is removed from a `GVGroupLayer`.
   *
   * When a child layer is removed from a group, this handler recalculates the
   * bounds of the group layer to reflect the updated set of children. The
   * recalculated bounds are then stored and may propagate upward in the
   * layer hierarchy.
   *
   * @param sender - The group layer from which the child layer was removed
   * @param event - The event payload containing information about the layer removal
   */
  #handleDomainLayerGroupLayerRemoved(sender: LayerDomain, event: DomainLayerGroupChildrenUpdatedEvent): void {
    // Calculate the bounds on the group layer which had a layer removed
    LayerController.initBoundsForLayerAndParentsAndForget(
      this.getMapId(),
      event.layer,
      this.getMapViewer().getProjection(),
      MapViewer.DEFAULT_STOPS
    );
  }

  /**
   * Handles when a raster layer failed to render an image on the map and we're trying to rescue it on-the-fly before officializing the error.
   *
   * This callback is useful when either (1) a WMS doesn't officially support the map projection, but we still want to attempt to pull an
   * image and put it on the map or (2) when the layer metadata has expired (time dimension) and needs to be refreshed.
   *
   * @param sender - The layer domain that fired the event
   * @param event - The error event which happened when the image tried to be rendered
   * @returns A promise that resolves with true if the rescue was a success, false if we let it fail and the layer should turn to error status
   */
  #handleDomainLayerImageLoadRescue(sender: LayerDomain, event: DomainLayerImageLoadRescueEvent): Promise<boolean> {
    // Try WMS projection rescue first.
    if (this.#attemptWmsProjectionRescue(event)) {
      return Promise.resolve(true);
    }

    // Try time-dimension metadata rescue as fallback.
    return this.#attemptTimeDimensionRescue(event);
  }

  /**
   * Handles when a layer's WMS style has changed in the domain.
   *
   * @param sender - The layer domain that emitted the event
   * @param event - The WMS style changed event
   */
  #handleDomainLayerWMSStyleChanged(sender: LayerDomain, event: DomainLayerWMSStyleChangedEvent): void {
    // Update the store
    setStoreLayerWmsStyle(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.wmsStyleName);

    // Trigger legend re-query through the layer set system (forced refresh)
    this.getControllersRegistry().layerSetController.legendsLayerSet.queryLegend(event.layer, true);
  }

  /**
   * Handles when a layer's raster function has changed in the domain.
   *
   * @param sender - The layer domain that emitted the event
   * @param event - The raster function changed event
   */
  #handleDomainLayerRasterFunctionChanged(sender: LayerDomain, event: DomainLayerRasterFunctionChangedEvent): void {
    // Update the store
    setStoreLayerRasterFunction(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.functionId);

    // Trigger legend re-query through the layer set system (forced refresh)
    this.getControllersRegistry().layerSetController.legendsLayerSet.queryLegend(event.layer, true);
  }

  /**
   * Handles when a layer's mosaic rule has changed in the domain.
   *
   * @param sender - The layer domain that emitted the event
   * @param event - The mosaic rule changed event
   */
  #handleDomainLayerMosaicRuleChanged(sender: LayerDomain, event: DomainLayerMosaicRuleChangedEvent): void {
    // Update the store
    setStoreLayerMosaicRule(this.getMapId(), event.layer.getLayerPath(), event.layerEvent.mosaicRule);

    // Trigger legend re-query through the layer set system (forced refresh)
    this.getControllersRegistry().layerSetController.legendsLayerSet.queryLegend(event.layer, true);
  }

  // #endregion DOMAIN HANDLERS

  // #region PRIVATE METHODS

  /**
   * Updates the inVisibleRange property for all layers based on the current map zoom level and resolution,
   * which is used to determine if a layer should be visible or not based on its scale thresholds.
   */
  #updateLayersVisibleInRange(): void {
    // Get the map viewer
    const mapViewer = this.getMapViewer();

    // Get the zoom
    const zoom = mapViewer.getView().getZoom();
    if (!zoom) return;

    // Get all layers
    const allLayers = this.getGeoviewLayers();

    // Current map resolution used for resolution-based visibility checks.
    const currentResolution = mapViewer.getView().getResolution() ?? mapViewer.getView().getResolutionForZoom(zoom);

    // Get the inVisibleRange property by checking each layer's OL resolution thresholds.
    allLayers.forEach((layer) => {
      // Check if the layer is in visible range
      const inVisibleRange = layer.isInVisibleRange(currentResolution);

      // Save to the store
      this.setLayerInVisibleRange(layer.getLayerPath(), inVisibleRange);
    });
  }

  /**
   * Attempts to rescue an image load failure on a WMS layer by overriding CRS.
   *
   * @param event - The image load rescue event
   * @returns True when a rescue was attempted successfully
   */
  #attemptWmsProjectionRescue(event: DomainLayerImageLoadRescueEvent): boolean {
    // Only WMS layers are eligible for this rescue path.
    if (!(event.layer instanceof GVWMS)) return false;

    // Get the supported CRSs
    const supportedCRSs = event.layer.getLayerConfig().getSupportedCRSs();
    const mapProj = this.getMapViewer().getProjection().getCode();

    // If the projection is already supported, this rescue path does not apply.
    if (supportedCRSs.includes(mapProj)) return false;

    // If we're already overriding, avoid retry loops.
    if (event.layer.getOverrideCRS()) return false;

    // Log warning
    logger.logWarning(`The map projection '${mapProj}' is not officially supported by the layer '${event.layer.getLayerPath()}'...`);

    // Get prioritized lists of projections to retry with (we want to attempt with higher priority projections)
    const highlyPrioritizedProjections = VALID_PROJECTION_CODES.map((projCode) => `EPSG:${projCode}`);
    const moderatePrioritizedProjections = Object.values(Projection.PROJECTION_NAMES);

    // Attempt to find a suitable CRS
    const selectedCRS =
      highlyPrioritizedProjections.find((crs) => supportedCRSs.includes(crs)) ||
      moderatePrioritizedProjections.find((crs) => supportedCRSs.includes(crs)) ||
      supportedCRSs[0];

    // If no fallback CRS was found, this rescue path fails.
    if (!selectedCRS) return false;

    // Override the CRS in the layer
    event.layer.setOverrideCRS({
      layerProjection: selectedCRS,
      mapProjection: mapProj,
    });

    // Notify the user
    this.getMapViewer().notifications.showWarning('warning.layer.layerCRSNotSupported', { mapProj, layerName: event.layer.getLayerName() });

    // Force a refresh so the layer gets drawn with the overridden CRS
    event.layer.refresh(this.getMapViewer().getProjection());

    // Rescued
    return true;
  }

  /**
   * Attempts to rescue an image load failure by refreshing expired temporal metadata.
   *
   * @param event - The image load rescue event
   * @returns A promise that resolves with true when rescue succeeded, else false
   */
  async #attemptTimeDimensionRescue(event: DomainLayerImageLoadRescueEvent): Promise<boolean> {
    // Get the layer config
    const layerConfig = event.layer.getLayerConfig();

    // If the layer has no temporal dimension, this rescue path does not apply.
    if (!layerConfig.getTimeDimension()) return false;

    // If metadata has been refreshed fairly recently already, let the layer fail naturally.
    const dateLastRefreshIfAny = this.#layersBeingMetadataRefreshed[layerConfig.layerPath];
    if (dateLastRefreshIfAny && Date.now() - dateLastRefreshIfAny < LayerController.#METADATA_REFRESHED_TIMEOUT) return false;

    // Refresh metadata on-the-fly and retry time slider registration.
    this.#layersBeingMetadataRefreshed[layerConfig.layerPath] = Date.now();
    await layerConfig.refreshMetadata(this.#uiDomain.getDisplayDateMode());
    this.getControllersRegistry().timeSliderController?.tryRegisterLayer(event.layer);

    // Force a refresh so the layer gets drawn again to confirm if the layer was rescued or not by the metadata refresh operation
    event.layer.refresh(undefined);

    // Rescued
    return true;
  }

  /**
   * Registers layer information for the ordered layers in the store.
   *
   * @param layerConfig - The layer configuration to be reordered
   */
  #registerForOrderedLayerInfo(layerConfig: ConfigBaseClass): void {
    // If the map index for the given layer path hasn't been set yet
    if (getStoreLayerOrderedLayerIndexByPath(this.getMapId(), layerConfig.layerPath) === -1) {
      // Get the parent layer path
      const parentLayerPathArray = layerConfig.layerPath.split('/');
      parentLayerPathArray.pop();
      const parentLayerPath = parentLayerPathArray.join('/');

      // Get the parent layer config, if any
      const parentLayerConfig = layerConfig.getParentLayerConfig();

      // If the map index of a parent layer path has been set and it is a valid UUID (or a suffixed UUID like uuid:suffix
      // for duplicate geocore layers), the ordered layer entry is a placeholder registered while the geocore layer info was fetched
      const parentLayerPathBase = parentLayerPath.includes(':') ? parentLayerPath.split(':')[0] : parentLayerPath;
      if (getStoreLayerOrderedLayerIndexByPath(this.getMapId(), parentLayerPath) !== -1 && isValidUUID(parentLayerPathBase)) {
        // Replace the placeholder ordered layer paths
        this.replaceOrderedLayerPaths(layerConfig, parentLayerPath);
      } else if (parentLayerConfig) {
        // Here the map index of a sub layer path hasn't been set and there's a parent layer config for the current layer config
        // Get the map index of the parent layer path
        const parentLayerIndex = getStoreLayerOrderedLayerIndexByPath(this.getMapId(), parentLayerPath);

        // Get the number of layers
        const numberOfLayers = utilFindLayerAndChildrenPaths(parentLayerPath, getStoreLayerOrderedLayerPaths(this.getMapId())).length;

        // If the map index of the parent has been set
        if (parentLayerIndex !== -1) {
          // Add the ordered layer paths for the sub layer path based on the parent index + the number of child layers
          this.addOrderedLayerPathsByConfig(layerConfig as TypeLayerEntryConfig, parentLayerIndex + numberOfLayers);
        } else {
          // If we get here, something went wrong and we have a sub layer being registered before the parent
          logger.logError(`Sub layer ${layerConfig.layerPath} registered in layer order before parent layer`);
          this.addOrderedLayerPathsByConfig(parentLayerConfig);
        }
      } else {
        // Add the ordered layer paths for layer that hasn't been set and has no parent layer or geocore placeholder
        this.addOrderedLayerPathsByConfig(layerConfig as TypeLayerEntryConfig);
      }
    }
  }

  /**
   * Updates the mosaicRule for a layer by merging new properties.
   *
   * @param layerPath - The layer path
   * @param partialMosaicRule - An object with one or more mosaicRule properties to update
   */
  #setLayerMosaicRuleProperty(layerPath: string, partialMosaicRule: Partial<TypeMosaicRule>): void {
    const prevRule = getStoreLayerMosaicRule(this.getMapId(), layerPath);
    if (!prevRule) return;

    // Merge the existing mosaic rule with the new properties, ensuring required properties are preserved
    const mergedRule: TypeMosaicRule = {
      ...prevRule,
      ...partialMosaicRule,
      mosaicMethod: partialMosaicRule.mosaicMethod ?? prevRule.mosaicMethod ?? 'esriMosaicNone',
      mosaicOperation: partialMosaicRule.mosaicOperation ?? prevRule.mosaicOperation ?? 'MT_FIRST',
    };

    this.setLayerMosaicRule(layerPath, mergedRule);
  }

  /**
   * Retrieves the timestamp when a layer started its deletion process.
   *
   * @param layerPath - Unique path identifying the layer within the map
   * @returns The timestamp (in ms) when deletion started, or `undefined`
   * if the layer is not currently pending deletion
   */
  #getLayerBeingDeleted(layerPath: string): LayerDeletionJob | undefined {
    return this.#layersBeingDeleted?.[layerPath];
  }

  /**
   * Marks a layer as being in the deletion process.
   *
   * @param layerPath - Unique path identifying the layer within the map
   * @param job - The deletion job details
   */
  #addLayerBeingDeleted(layerPath: string, job: LayerDeletionJob): void {
    // Add the layer for deletion
    this.#layersBeingDeleted[layerPath] = job;
  }

  /**
   * Removes a layer from the pending deletion list and clears its
   * deletion progress indicator from the UI store.
   *
   * @param layerPath - Unique path identifying the layer within the map
   */
  #removeLayerBeingDeleted(layerPath: string): void {
    // Update the store
    setStoreLayerDeletionStartTime(this.getMapId(), layerPath, undefined);

    // Remove the layer from deletion
    delete this.#layersBeingDeleted[layerPath];
  }

  // #endregion PRIVATE METHODS

  // #region STATIC METHODS

  /**
   * Starts bounds calculation and stores bounds as they get calculated.
   *
   * Uses a fire-and-forget pattern and handles completion or failure in promise handlers.
   *
   * @param mapId - The unique identifier of the map instance
   * @param gvLayer - The layer from which bounds recalculation should begin
   * @param projection - The projection to initialize the bounds into
   * @param stops - Number of interpolation stops for bounds initialization
   */
  static initBoundsForLayerAndParentsAndForget(mapId: string, gvLayer: AbstractBaseGVLayer, projection: OLProjection, stops: number): void {
    // Redirect and forget about it
    const promise = LayerController.#initBoundsForLayerAndParents(mapId, gvLayer, projection, stops);
    promise
      .then((_bounds) => {
        // Log for troubleshooting, leave the line here commented
        // logger.logDebug('BOUNDS CALCULATED AND STORED', gvLayer.getLayerPath(), bounds);
      })
      .catch((error: unknown) => {
        // Log the error
        logger.logPromiseFailed('in LayerController.initBoundsForLayerAndParentsAndForget', error);
      });
  }

  /**
   * Recalculates and stores bounds for a layer and all of its parent groups.
   *
   * Initializes bounds for the provided layer, then walks up the parent hierarchy.
   *
   * @param mapId - The unique identifier of the map instance
   * @param gvLayer - The starting layer for which bounds should be computed
   * @param projection - The projection to initialize the bounds into
   * @param stops - Number of interpolation stops for bounds initialization
   * @returns A promise that resolves with the bounds computed for the input layer, or undefined
   */
  static async #initBoundsForLayerAndParents(
    mapId: string,
    gvLayer: AbstractBaseGVLayer,
    projection: OLProjection,
    stops: number
  ): Promise<Extent | undefined> {
    // Walk current layer + parents upward once
    let current: AbstractBaseGVLayer | undefined = gvLayer;
    let returnedBounds: Extent | undefined;
    while (current) {
      // Get the bounds of the layer
      // Must await sequentially: parent bounds depend on child bounds
      // eslint-disable-next-line no-await-in-loop
      const bounds = await current.initBounds(projection, stops);

      // Store it
      setStoreLayerBounds(mapId, current.getLayerPath(), current.getBounds(), current.getBoundsLonLat());

      // If the current layer is the layer we were calculating bounds for
      returnedBounds = bounds;

      // Advance to parent
      current = current.getParent();
    }

    // Return the bounds on the layer we were calculating the bounds for
    return returnedBounds;
  }

  // #endregion STATIC METHODS

  // #region EVENTS

  /**
   * Emits layer item visibility toggled event.
   *
   * @param event - The event to emit
   */
  #emitLayerItemVisibilityChanged(event: ControllerLayerItemVisibilityChangedEvent): void {
    // Emit the event for all handlers
    EventHelper.emitEvent(this, this.#onLayerItemVisibilityToggledHandlers, event);
  }

  /**
   * Registers a layer item visibility toggled event handler.
   *
   * @param callback - The callback to be executed whenever the event is emitted
   */
  onLayerItemVisibilityChanged(callback: ControllerLayerItemVisibilityChangedDelegate): void {
    // Register the event handler
    EventHelper.onEvent(this.#onLayerItemVisibilityToggledHandlers, callback);
  }

  /**
   * Unregisters a layer item visibility toggled event handler.
   *
   * @param callback - The callback to stop being called whenever the event is emitted
   */
  offLayerItemVisibilityChanged(callback: ControllerLayerItemVisibilityChangedDelegate): void {
    // Unregister the event handler
    EventHelper.offEvent(this.#onLayerItemVisibilityToggledHandlers, callback);
  }

  // #endregion EVENTS
}

/** Represents a pending layer deletion job with its undo state. */
type LayerDeletionJob = {
  /** The delayed job that controls the deletion timer. */
  delayedJob: DelayJob;

  /** The original visibility of the layer before the deletion process started. */
  originalVisibility: boolean;
};

/** Defines the event payload for the layer item visibility changed delegate. */
export interface ControllerLayerItemVisibilityChangedEvent {
  /** The affected layer. */
  layer: AbstractGVLayer;

  /** The item being changed. */
  item: TypeLegendItem;

  /** The new visibility. */
  visible: boolean;
}

/** Defines a delegate for the layer item visibility changed event handler function signature. */
export type ControllerLayerItemVisibilityChangedDelegate = EventDelegateBase<
  LayerController,
  ControllerLayerItemVisibilityChangedEvent,
  void
>;
